import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { contactNameDisplay } from "@/lib/vocabulary";
import { useParams, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { parseCustomerTab } from "./tabs";
import { BatchBar } from "./batch-bar";
import { BatchChargeBar } from "./invoice-batch/batch-charge-bar";
import { CustomerPaymentStrip } from "@/components/customer-payment-settings";
import { EstimateBatchBar, EstimateSelectAllBar } from "./estimate-batch/estimate-batch-bar";
import { EstimateSendDialog } from "./estimate-batch/estimate-send-dialog";
import {
    isSendable,
    selectionScopeLabel as estimateScopeLabel,
    unsendableReason,
} from "./estimate-batch/estimate-batch-copy";
import { isChargeable, selectionScopeLabel, unselectableReason } from "./invoice-batch/batch-charge-copy";
import { selectAllScopeLabel } from "./batch-actions";
import { BackLink } from "@/components/back-link";
import { TaskCreateButton } from "@/components/task-create-dialog";
import { TaskListInline } from "@/components/task-list-inline";
import { FromTheFieldSection } from "@/components/field-add/field-add-office";
import { DetailErrorState } from "@/components/detail-error-state";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { api } from "@/lib/api";
import { CustomerTypeHeaderPick, customerTypeWord } from "@/components/customer-home-or-business";
import { cn, statusColor, formatDate, formatDateTime, formatDollars, pluralize } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatementDialog } from "@/components/statement-dialog";
import { DataTable, openRowColumn, type Column } from "@/components/ui/data-table";
import { SortScopeNote } from "@/components/ui/sort-scope-note";
import { dateSortValue, moneySortValue } from "@/lib/table-sort";
import { usePersistedSort } from "@/lib/use-persisted-sort";
import {
    BucketStrip,
    LocationsTable,
    LOCATION_SORT_KEYS,
    type LocationSortKey,
    type SiteBucketChip,
    SiteCell,
} from "./locations-table";
import type { SiteRollup } from "@/lib/site-rollup";
import { JOB_STATUSES, statusLabel } from "@/lib/vocabulary";
import { StatCard } from "@/components/ui/stat-card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BillingBudgetTab } from "./budget/billing-budget-tab";
import { BudgetChip } from "./budget/chip";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    buildRemovalMessage,
    contactActionAvailability,
    removeContactsInBatches,
    summarizeRemoval,
} from "./contactRemoval";
import { Dialog } from "@/components/ui/dialog";
import { ContactFormDialog } from "@/components/contact-form-dialog";
import { ActivityTimeline } from "@/components/activity-timeline";
import { WoPdfHoverPreview } from "@/components/wo-pdf-hover-preview";
import CardOnFilePanel from "@/components/card-on-file-panel";
import { CustomerPortalAccessPanel } from "@/components/customer-portal-access-panel";
import { CallLogList } from "@/pages/call-logs/index";
import { CustomerBillingPanel } from "@/components/customer-billing-panel";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { LocationPickerMap } from "@/components/location-picker-map";
import {
    WaterReportingFields,
    buildWaterReportingPatch,
    invalidateWaterReporting,
    useWaterProviders,
    type WaterProviderOption,
    type WaterReportingValue,
} from "@/components/water-reporting-editor";
import { InterviewsCard } from "./interviews-card";
import { DefaultForTags, MakeDefaultForPicker, WoDefaultsStrip, type DefaultGroup } from "./wo-default-forms";
import { ContactsRoutingTab } from "./contacts-routing-tab";
import { InvoiceBounceMark, type InvoiceBounce } from "@/components/invoice-bounce-mark";
import { CustomerRulesTab } from "./customer-rules-tab";
import { useFinishLineSwitch } from "@/lib/finish-line";
import {
    ArrowLeft,
    Building,
    Briefcase,
    CheckSquare,
    FileText,
    Receipt,
    Users,
    ScrollText,
    ListChecks,
    PiggyBank,
    Activity,
    Phone,
    Mail,
    Star,
    DollarSign,
    ClipboardList,
    UserPlus,
    Sparkles,
    Loader2,
    Wallet,
    Plus,
    CalendarPlus,
    Check,
    ExternalLink,
    Sparkle,
    X,
    AlertTriangle,
    MapPin,
    Save,
    Pencil,
    Upload,
    FileStack,
    Wand2,
    Settings,
    Trash2,
    ShieldCheck,
    Network,
} from "lucide-react";
import { canManageServiceHold } from "@/lib/service-hold";
import { isContactStale, formatVerifiedLabel } from "@/lib/contactFreshness";
import { openPriceCheck } from "@/components/price-check";
import { PaymentLookup } from "@/components/payment-lookup";
import {
    NO_BACKFLOW_TYPE_MESSAGE,
    pickBackflowTestJobType,
    type JobTypeOption as FirstTestJobType,
} from "./first-test-job-type";
import { CameFromChip } from "@/components/came-from-chip";
import type { CameFromRecord } from "@/lib/cameFrom";

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

interface Customer {
    id: string;
    company_name: string;
    // Where they came from (migration 711): the website's record, or the
    // office's "How did they hear about us?" answer. Null = not known.
    came_from?: CameFromRecord | null;
    heard_about?: string | null;
    heard_about_detail?: string | null;
    customer_type: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    locationCount: number;
    jobCount: number;
    openJobCount: number;
    // Omitted by the API for callers without financials.view.
    revenueCollected?: number;
    outstandingAmount: number;
    unpaidInvoiceCount: number;
    devicesDue90d?: number;
    /** #992 — split so past-due never hides inside "due in 90 days". */
    devicesOverdue?: number;
    devicesDueSoon?: number;
    lastTestAt?: string | null;
    nextScheduledVisit?: string | null;
    payment_profile?: string | null;
    net_terms_days?: number | null;
    /** #887 — jobs for this account can't be completed without a WO reference. */
    requires_customer_wo?: boolean;
    /** #905 — collections tier: hand_managed accounts are never auto-emailed. */
    collections_mode?: "auto" | "hand_managed";
    /** #1030 — "Do Not Service": warns before new work, silences auto-work. */
    service_hold?: boolean;
    service_hold_reason?: string | null;
    service_hold_set_at?: string | null;
    service_hold_set_by_name?: string | null;
    tax_status?: string | null;
    tax_exempt_reason?: string | null;
    has_card_on_file?: boolean;
    stripe_payment_method_brand?: string | null;
    stripe_payment_method_last4?: string | null;
    priority_tier?: string | null;
    monthly_batch_target_override?: number | null;
    notes?: string | null;
    billing_address_line1?: string | null;
    billing_address_line2?: string | null;
    billing_city?: string | null;
    billing_state?: string | null;
    billing_zip?: string | null;
}

interface JobRow {
    id: string;
    display_number: string | null;
    title: string;
    status: string;
    assigned_to_name: string | null;
    scheduled_start: string | null;
    trade_name: string | null;
    created_at: string;
    /** #992 — the site was always joined; its name was never selected. */
    site_id: string | null;
    site_name: string | null;
    site_city: string | null;
}

export interface EstimateRow {
    id: string;
    display_number: string | null;
    title: string | null;
    status: string;
    subtotal: string;
    created_at: string;
    site_id: string | null;
    site_name: string | null;
    site_city: string | null;
    /** #1349 — the WO/PO the customer files by; also names the zipped PDF. */
    wo_number?: string | null;
}

export interface InvoiceRow {
    id: string;
    invoice_number: string | null;
    title: string | null;
    status: string;
    total_amount: string;
    amount_paid: string;
    created_at: string;
    // #894 — the columns a customer needs to match a line.
    balance?: string | null;
    due_date?: string | null;
    days_late?: number | null;
    site_id?: string | null;
    site_name?: string | null;
    site_city?: string | null;
    wo_number?: string | null;
    /** The invoice email that didn't arrive (lifecycle audit 10/29). */
    bounce?: InvoiceBounce | null;
}

interface ContactRow {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    role_title: string | null;
    is_primary: boolean;
    sms_consent?: string | null;
    sms_consent_at?: string | null;
    sms_consent_source?: string | null;
    last_verified_at?: string | null;
    last_verified_by_name?: string | null;
}

// New contact-hierarchy read model (GET /customers/:id/contact-methods).
// The Contacts tab reads from here — the merged view of office-authored
// contacts AND full-fidelity ServiceTitan imports — while add/edit/delete
// still go through the legacy customer_contacts CRUD (dual-written
// server-side into this hierarchy).
interface ContactMethodItem {
    id: string;
    kind: string;
    value: string;
    label: string | null;
    memo: string | null;
    fromServiceTitan: boolean;
    stSortOrder: number | null;
    isPrimary: boolean;
    purposes: string[];
}

interface HierarchyContact {
    id: string;
    firstName: string | null;
    lastName: string | null;
    roleTitle: string | null;
    siteId: string | null;
    isPrimary: boolean;
    isBilling: boolean;
    source: string;
    sourceContactId: string | null;
    lastVerifiedAt: string | null;
    lastVerifiedByName: string | null;
    methods: ContactMethodItem[];
}

// Display adapter: hierarchy contact flattened for the tab's table +
// overview card. `legacyId` (customer_contacts.id) drives edit/delete/
// verify; ST-imported contacts without a legacy anchor are read-only.
interface ContactDisplayRow {
    id: string;
    legacyId: string | null;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    role_title: string | null;
    is_primary: boolean;
    fromServiceTitan: boolean;
    methods: ContactMethodItem[];
    last_verified_at: string | null;
    last_verified_by_name: string | null;
}

// Saved routing purposes (contact_method_purposes) shown as chips on the
// Contacts tab. Written by the Approve & Send modal's "remember routing".
const PURPOSE_LABEL: Record<string, string> = {
    send_invoices: "Invoices",
    send_quotes: "Estimates",
    send_reports: "Reports",
    send_appointment: "Appointments",
    send_reminders: "Reminders",
};

function toContactDisplayRow(c: HierarchyContact): ContactDisplayRow {
    return {
        id: c.id,
        legacyId: c.sourceContactId,
        first_name: c.firstName?.trim() || "—",
        last_name: c.lastName,
        email: c.methods.find((m) => m.kind === "email")?.value ?? null,
        phone: c.methods.find((m) => m.kind === "phone")?.value ?? null,
        role_title: c.roleTitle,
        is_primary: c.isPrimary,
        fromServiceTitan: c.source === "st_import",
        methods: c.methods,
        last_verified_at: c.lastVerifiedAt,
        last_verified_by_name: c.lastVerifiedByName,
    };
}

interface AgreementRow {
    id: string;
    title: string;
    status: string;
    start_date: string;
    end_date: string | null;
    renewal_type: string;
    billing_cycle: string;
    amount: string | null;
}

/**
 * Shape of the location state pushed by the New Customer Intake page
 * after a successful create. When present, we render the
 * <IntakeHandoffBanner /> at the top of the customer detail page so the
 * office can pick the most likely next step (queue first test, start a
 * job, send a quote) without bouncing back to a separate success screen.
 */
interface IntakeHandoffState {
    intakeHandoff?: {
        siteId: string;
        intakeNotes: string;
        similar: Array<{
            id: string;
            company_name: string;
            match_reason: string;
        }>;
        companyName: string;
    };
}

// ────────────────────────────────────────────────────────────
// Tab toolbar — shared by Jobs / Estimates / Invoices / Contacts / Agreements
// ────────────────────────────────────────────────────────────

/**
 * The same little "search + optional status filter" toolbar we
 * use across every customer-detail tab. Keeping it inline (rather
 * than a separate file) so this page reads top-to-bottom for
 * anyone touching it next.
 */
interface TabToolbarProps {
    searchValue: string;
    onSearchChange: (v: string) => void;
    searchPlaceholder: string;
    statusValue?: string;
    onStatusChange?: (v: string) => void;
    statusOptions?: Array<{ value: string; label: string }>;
    rightSlot?: ReactNode;
}

function TabToolbar({
    searchValue,
    onSearchChange,
    searchPlaceholder,
    statusValue,
    onStatusChange,
    statusOptions,
    rightSlot,
}: TabToolbarProps) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-0">
                <Input
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="pr-8"
                />
                {searchValue.length > 0 && (
                    <button
                        type="button"
                        onClick={() => onSearchChange("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/60 hover:text-foreground"
                        aria-label="Clear search"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>
            {statusOptions && onStatusChange && (
                <Select
                    value={statusValue ?? ""}
                    onChange={(e) => onStatusChange(e.target.value)}
                    className="w-full sm:w-44"
                >
                    <option value="">All statuses</option>
                    {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </Select>
            )}
            {rightSlot}
        </div>
    );
}

// #992 — this list was hand-maintained and had drifted: it offered "on_hold"
// and "pending_closeout", neither of which any job has ever had, while omitting
// awaiting_scheduling, office_review, held and closed, which are the statuses
// the office actually filters for. Same failure #894 fixed on invoices —
// a filter that silently matches nothing. Derived from the canonical list now,
// which a parity test keeps in step with the server's status machine.
const JOB_STATUS_OPTIONS = JOB_STATUSES.filter((s) => s !== "draft" && s !== "unqueued").map((value) => ({
    value,
    label: statusLabel(value),
}));

// The real `estimates.status` CHECK is draft/sent/accepted/declined/cancelled
// (docker/init, batch 4). "Approved", "Rejected" and "Expired" were offered
// here and match no row on any account, ever.
/**
 * What's still owed on an invoice line. The server sends `balance` on the
 * rows that have it; older rows fall back to total − paid. One function so
 * the Balance cell and the Balance sort are the same number.
 */
export function invoiceBalance(r: InvoiceRow): number {
    return Number(r.balance ?? Number(r.total_amount || 0) - Number(r.amount_paid || 0));
}

const ESTIMATE_STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "accepted", label: "Accepted" },
    { value: "declined", label: "Declined" },
    { value: "cancelled", label: "Cancelled" },
];

// #894 — these mirror the REAL invoice statuses (draft/open/partially_paid/
// paid/void). The old list (sent/partial/overdue) silently matched nothing.
// "unpaid" is a server-side pseudo-status covering open + partially_paid, and
// it's the tab's default: the office's first question is "what's owed?".
const INVOICE_STATUS_OPTIONS = [
    { value: "unpaid", label: "Unpaid" },
    { value: "draft", label: "Draft" },
    { value: "open", label: "Awaiting payment" },
    { value: "partially_paid", label: "Partially paid" },
    { value: "paid", label: "Paid" },
    { value: "void", label: "Cancelled" },
];

// ────────────────────────────────────────────────────────────
// Hooks
// ────────────────────────────────────────────────────────────

/**
 * Simple debounce for free-text search inputs. We want the user to be
 * able to type at full speed without hammering the API on every
 * keystroke, but still feel responsive (~250 ms is the sweet spot for
 * customer-detail tabs where most lists are short).
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

/*
 * ── The Estimates / Invoices tables: every header sorts ───────────────
 *
 * These column definitions live at MODULE scope, not inside the component.
 * Nothing in them reads component state, and DataTable memoises its sort on
 * the `columns` identity — rebuilt every render, that memo never hits and
 * the whole list re-sorts on every keystroke in the search box. Hoisted,
 * the identity is stable and the sort accessors are directly testable.
 *
 * The accessors deliberately do NOT read the rendered cell. `formatDollars`
 * would sort $90 above $1,200 and `formatDate` would sort "Aug 2026" above
 * "Jan 2027", so each accessor takes the raw column through the shared
 * helpers in `lib/table-sort`. Where a cell falls back to a placeholder
 * ("Untitled", "#a1b2c3d4"), the accessor falls back the same way, so the
 * sorted order is always the order you can read on screen.
 *
 * No default sort is set: the list stays in the server's newest-first order
 * until someone clicks a header.
 */
export const estimateCols: Column<EstimateRow>[] = [
    {
        key: "id",
        header: "ID",
        className: "w-20",
        sortable: true,
        sortAccessor: (r) => r.display_number || r.id.slice(0, 8),
        render: (r) => <span className="font-mono text-xs">#{r.display_number || r.id.slice(0, 8)}</span>,
    },
    {
        key: "title",
        header: "Title",
        sortable: true,
        sortAccessor: (r) => r.title ?? "Untitled",
        render: (r) => <span>{r.title ?? "Untitled"}</span>,
    },
    {
        key: "site_name",
        header: "Location",
        sortable: true,
        sortAccessor: (r) => r.site_name,
        render: (r) => <SiteCell siteId={r.site_id} siteName={r.site_name} city={r.site_city} />,
    },
    {
        // #1349 — the WO/PO number the customer files by. It was already on
        // the job; the tab just never showed it, so the office couldn't tell
        // two quotes for the same store apart without opening them.
        key: "wo_number",
        header: "WO #",
        sortable: true,
        sortAccessor: (r) => r.wo_number ?? "",
        render: (r) =>
            r.wo_number ? (
                <span className="font-mono text-xs">{r.wo_number}</span>
            ) : (
                <span className="text-muted-foreground">—</span>
            ),
    },
    {
        key: "status",
        header: "Status",
        sortable: true,
        sortAccessor: (r) => r.status,
        render: (r) => (
            <Badge className={statusColor(r.status)} variant="outline">
                {r.status}
            </Badge>
        ),
    },
    {
        key: "subtotal",
        header: "Subtotal",
        sortable: true,
        firstSortDir: "desc",
        sortAccessor: (r) => moneySortValue(r.subtotal),
        render: (r) => <span className="font-medium tabular-nums">{formatDollars(Number(r.subtotal || 0))}</span>,
    },
    {
        key: "created_at",
        header: "Date",
        sortable: true,
        firstSortDir: "desc",
        sortAccessor: (r) => dateSortValue(r.created_at),
        render: (r) => <span className="text-xs text-foreground/80">{formatDate(r.created_at)}</span>,
    },
];

/* Same rule as the estimates table above. */
export const invoiceCols: Column<InvoiceRow>[] = [
    {
        key: "invoice_number",
        header: "#",
        className: "w-24",
        sortable: true,
        // Digit-aware collation (see lib/table-sort) is what keeps INV-9
        // ahead of INV-10 here instead of behind it.
        sortAccessor: (r) => r.invoice_number ?? `#${r.id.slice(0, 8)}`,
        render: (r) => (
            <span className="inline-flex flex-wrap items-center gap-1">
                <Link to={`/invoices/${r.id}`} className="font-mono text-xs text-primary hover:underline">
                    {r.invoice_number ?? `#${r.id.slice(0, 8)}`}
                </Link>
                <InvoiceBounceMark bounce={r.bounce} />
            </span>
        ),
    },
    {
        key: "title",
        header: "Title",
        sortable: true,
        sortAccessor: (r) => r.title ?? "Untitled",
        render: (r) => (
            <Link to={`/invoices/${r.id}`} className="hover:underline">
                {r.title ?? "Untitled"}
            </Link>
        ),
    },
    {
        key: "status",
        header: "Status",
        sortable: true,
        sortAccessor: (r) => r.status,
        render: (r) => (
            <Badge className={statusColor(r.status)} variant="outline">
                {r.status?.replace(/_/g, " ")}
            </Badge>
        ),
    },
    {
        key: "total_amount",
        header: "Total",
        sortable: true,
        firstSortDir: "desc",
        sortAccessor: (r) => moneySortValue(r.total_amount),
        render: (r) => <span className="font-medium tabular-nums">{formatDollars(Number(r.total_amount || 0))}</span>,
    },
    // #894 — the audit's missing columns: what's still owed on the line,
    // when it's due (and how late), which site, and the customer's WO #.
    {
        key: "balance",
        header: "Balance",
        sortable: true,
        firstSortDir: "desc",
        // Sorts on the same derivation the cell prints, so "sort by
        // Balance" and "read the Balance column" can never disagree.
        sortAccessor: (r) => invoiceBalance(r),
        render: (r) => {
            const balance = invoiceBalance(r);
            return (
                <span className={`font-semibold tabular-nums ${balance > 0 ? "" : "text-muted-foreground"}`}>
                    {formatDollars(balance)}
                </span>
            );
        },
    },
    {
        key: "due_date",
        header: "Due",
        sortable: true,
        firstSortDir: "desc",
        // "On receipt" (no due date) sorts last, with the other blanks.
        sortAccessor: (r) => dateSortValue(r.due_date),
        render: (r) => {
            const late = Number(r.days_late ?? 0);
            const unpaid = r.status === "open" || r.status === "partially_paid";
            return (
                <span className="text-xs tabular-nums">
                    {r.due_date ? formatDate(r.due_date) : "On receipt"}
                    {unpaid && late > 0 && (
                        <span className="ml-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800 dark:bg-red-950/40 dark:text-red-300">
                            {late}d late
                        </span>
                    )}
                </span>
            );
        },
    },
    {
        key: "site_name",
        header: "Location",
        sortable: true,
        sortAccessor: (r) => r.site_name,
        render: (r) => <SiteCell siteId={r.site_id} siteName={r.site_name} city={r.site_city} />,
    },
    {
        key: "wo_number",
        header: "WO #",
        sortable: true,
        sortAccessor: (r) => r.wo_number,
        render: (r) => <span className="text-xs tabular-nums text-foreground/80">{r.wo_number ?? "—"}</span>,
    },
    {
        key: "created_at",
        header: "Date",
        sortable: true,
        firstSortDir: "desc",
        sortAccessor: (r) => dateSortValue(r.created_at),
        render: (r) => <span className="text-xs text-foreground/80">{formatDate(r.created_at)}</span>,
    },
];

export default function CustomerDetailPage() {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Validated, not merely defaulted — see CUSTOMER_TABS above.
    const activeTab = parseCustomerTab(searchParams.get("tab"));

    /**
     * Jump from a header number to the list of locations behind it. A KPI you
     * cannot open is a fact you have to go re-derive somewhere else.
     */
    const goToLocationsBucket = useCallback(
        (bucket: string) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", "locations");
                if (bucket === "all") next.delete("bucket");
                else next.set("bucket", bucket);
                return next;
            });
        },
        [setSearchParams],
    );
    const rateCardId = searchParams.get("rateCard");
    const { isAdmin, can } = useAuth();
    const canFinancials = can(PERMISSIONS.financials.view);
    // Contacts & Routing tab (Prompt C): everything behind it is crm.manage
    // on the server, so don't show techs a tab that would just 403.
    const canCrmManage = can(PERMISSIONS.crm.manage);
    // Dollar amounts follow the Money page's gate (front-office never sees money).
    const canSeeMoney = can(PERMISSIONS.finance.view);
    // Teach it once (#1738): the Rules tab rides the new job page's switch
    // (office_review.manage + `job-page.finish-line` on), so switch off = no change.
    const showRules = useFinishLineSwitch().on;

    // Intake handoff: detail page was opened immediately after the New
    // Customer Intake form. We dismiss it locally (state-only) so a
    // refresh / back-nav doesn't re-show the banner once they've moved
    // on to the next thing.
    const incomingHandoff = (location.state as IntakeHandoffState | null)?.intakeHandoff ?? null;
    const [handoff, setHandoff] = useState(incomingHandoff);
    useEffect(() => {
        if (incomingHandoff && location.state) {
            // Strip the state from history so a refresh doesn't replay it,
            // but keep our local copy for this session.
            navigate(location.pathname, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Core customer data ──────────────────────────────────
    const {
        data: customerData,
        isLoading: loadingCustomer,
        error: customerError,
        refetch: refetchCustomer,
    } = useQuery({
        queryKey: ["customer", id],
        queryFn: () => api.get<{ customer: Customer }>(`/api/customers/${id}`),
        enabled: !!id,
    });
    const customer = customerData?.customer;

    // ── Per-tab search / filter state ─────────────────────────
    // Each tab gets a debounced free-text query and (where relevant)
    // a status filter. Jobs / estimates / invoices push the query to
    // the server (those endpoints support `?q` + `?status`). Contacts
    // and agreements are small lists that we filter client-side.
    const [jobsSearch, setJobsSearch] = useState("");
    const [jobsStatus, setJobsStatus] = useState("");
    const debouncedJobsSearch = useDebouncedValue(jobsSearch, 250); /* see hook at bottom of file */

    const [estimatesSearch, setEstimatesSearch] = useState("");
    const [estimatesStatus, setEstimatesStatus] = useState("");
    const debouncedEstimatesSearch = useDebouncedValue(estimatesSearch, 250);
    // #1349 — estimates picked to be emailed as one zip. Selecting is a mode
    // you turn on (the Contacts tab pattern), not always-on checkboxes: the
    // tab is read far more often than it is used to send.
    const [estimateSelectMode, setEstimateSelectMode] = useState(false);
    const [selectedEstimates, setSelectedEstimates] = useState<string[]>([]);
    const [estimateSendOpen, setEstimateSendOpen] = useState(false);

    const [invoicesSearch, setInvoicesSearch] = useState("");
    // #894 — the tab opens on what's owed, not three years of paid history
    // (Cintas: 3,030 invoices, 101 unpaid). "All statuses" is one click away.
    const [invoicesStatus, setInvoicesStatus] = useState("unpaid");
    // #1345 — invoices picked for a one-card batch charge. Held by the page
    // rather than the grid so it survives a refetch after the charge lands.
    const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
    const debouncedInvoicesSearch = useDebouncedValue(invoicesSearch, 250);

    const [contactsSearch, setContactsSearch] = useState("");
    const [agreementsSearch, setAgreementsSearch] = useState("");

    // ── Tabs data (lazy‑loaded by react-query, all enabled when id is set) ──
    const jobsQ = useQuery({
        queryKey: ["customer", id, "jobs", debouncedJobsSearch, jobsStatus],
        queryFn: () => {
            const params = new URLSearchParams();
            params.set("customerId", id ?? "");
            params.set("limit", "200");
            if (debouncedJobsSearch.trim()) params.set("q", debouncedJobsSearch.trim());
            if (jobsStatus) params.set("status", jobsStatus);
            return api.get<{ jobs: JobRow[]; total: number }>(`/api/jobs?${params.toString()}`);
        },
        enabled: !!id,
    });

    const estimatesQ = useQuery({
        queryKey: ["customer", id, "estimates", debouncedEstimatesSearch, estimatesStatus],
        queryFn: () => {
            const params = new URLSearchParams();
            params.set("customerId", id ?? "");
            params.set("limit", "200");
            if (debouncedEstimatesSearch.trim()) params.set("q", debouncedEstimatesSearch.trim());
            if (estimatesStatus) params.set("status", estimatesStatus);
            return api.get<{ estimates: EstimateRow[]; total: number }>(`/api/estimates?${params.toString()}`);
        },
        enabled: !!id,
    });

    const invoicesQ = useQuery({
        queryKey: ["customer", id, "invoices", debouncedInvoicesSearch, invoicesStatus],
        queryFn: () => {
            const params = new URLSearchParams();
            params.set("customerId", id ?? "");
            params.set("limit", "200");
            if (debouncedInvoicesSearch.trim()) params.set("q", debouncedInvoicesSearch.trim());
            if (invoicesStatus) params.set("status", invoicesStatus);
            return api.get<{ invoices: InvoiceRow[]; total: number }>(`/api/invoices?${params.toString()}`);
        },
        enabled: !!id,
    });

    // Reads the merged contact hierarchy (manual + ServiceTitan imports).
    // The key stays under ["customer", id, "contacts"] so the existing CRUD
    // invalidations refresh it.
    const contactsQ = useQuery({
        queryKey: ["customer", id, "contacts", "methods"],
        queryFn: () => api.get<{ contacts: HierarchyContact[] }>(`/api/customers/${id}/contact-methods`),
        enabled: !!id,
    });
    const contactRows = (contactsQ.data?.contacts ?? []).map(toContactDisplayRow);

    const agreementsQ = useQuery({
        queryKey: ["customer", id, "agreements"],
        queryFn: () => api.get<{ agreements: AgreementRow[] }>(`/api/customers/${id}/agreements`),
        enabled: !!id,
    });

    const qc = useQueryClient();

    // ── Contact management (add / edit / delete) ──────────────
    const [addContactOpen, setAddContactOpen] = useState(false);
    const [editingContact, setEditingContact] = useState<ContactDisplayRow | null>(null);
    // Removing contacts (#1307). Tick boxes exist only while editing — this
    // list is read far more often than it is edited, and on an account with
    // 1,700 rows a standing column of them beside a Remove button is an
    // accident waiting for a busy afternoon.
    const [contactsEditing, setContactsEditing] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
    const [removalProgress, setRemovalProgress] = useState<{ done: number; total: number } | null>(null);

    /**
     * Removes by the hierarchy `contacts.id`, NOT the legacy id. That is the
     * whole fix: every action on this tab used to be `disabled={!r.legacyId}`,
     * and an imported contact has no legacy row — so the delete was dead on
     * exactly the rows the office wanted gone. The bulk endpoint takes v2 ids
     * and handles one or a thousand.
     */
    const removeContacts = useMutation({
        mutationFn: (contactIds: string[]) =>
            removeContactsInBatches(id!, contactIds, {
                // Clearing a bad import can be well over a thousand rows. A
                // button that just says "Removing…" for two minutes looks
                // hung; the count is what tells the office it is working.
                onProgress: (done, total) => setRemovalProgress({ done, total }),
            }),
        onSettled: () => setRemovalProgress(null),
        onSuccess: (res) => {
            qc.invalidateQueries({ queryKey: ["customer", id, "contacts"] });
            qc.invalidateQueries({ queryKey: ["customer-contacts", id] });
            qc.invalidateQueries({ queryKey: ["customer", id, "contact-suppressions"] });
            // Only clear what actually went — a blocked contact stays ticked
            // so the office can see which ones still need dealing with.
            const gone = new Set(res.results.filter((r) => r.status !== "blocked").map((r) => r.contactId));
            setSelectedContacts((prev) => new Set([...prev].filter((cid) => !gone.has(cid))));
            const { tone, text } = summarizeRemoval(res);
            if (tone === "success") toast.success(text);
            else if (tone === "warning") toast.warning(text, { duration: 12000 });
            else toast.error(text, { duration: 12000 });
        },
        onError: () => toast.error("Couldn't remove those contacts"),
    });

    const verifyContact = useMutation({
        mutationFn: (contactId: string) => api.post(`/api/customers/${id}/contacts/${contactId}/verify`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", id, "contacts"] });
            toast.success("Contact confirmed as current");
        },
        onError: () => toast.error("Couldn't confirm the contact"),
    });

    // Prompt 3 "Remember routing": saved purposes drive the Approve & Send
    // recipient prefill. Removing one here stops the automatic routing.
    const removePurpose = useMutation({
        mutationFn: ({ methodId, purposeKey }: { methodId: string; purposeKey: string }) =>
            api.delete(`/api/customers/${id}/contact-methods/${methodId}/purposes/${purposeKey}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", id, "contacts"] });
            toast.success("Routing removed — this address will no longer be pre-selected for that document");
        },
        onError: () => toast.error("Couldn't remove the routing"),
    });

    // ── Customer notes inline edit ────────────────────────────
    const [notesEditing, setNotesEditing] = useState(false);
    const [notesDraft, setNotesDraft] = useState("");
    // #894 — the statement preview/send dialog behind the Outstanding tile.
    const [statementOpen, setStatementOpen] = useState(false);
    const saveNotes = useMutation({
        mutationFn: () => api.patch(`/api/customers/${id}`, { notes: notesDraft.trim() || null }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", id] });
            setNotesEditing(false);
            toast.success("Notes saved");
        },
        onError: () => toast.error("Couldn't save notes"),
    });

    // Client-side filter for contacts (small list, no server search).
    const filteredContacts = contactRows.filter((r) => {
        const q = contactsSearch.trim().toLowerCase();
        if (!q) return true;
        return (
            (r.first_name ?? "").toLowerCase().includes(q) ||
            (r.last_name ?? "").toLowerCase().includes(q) ||
            (r.role_title ?? "").toLowerCase().includes(q) ||
            r.methods.some((m) => m.value.toLowerCase().includes(q) || (m.memo ?? "").toLowerCase().includes(q))
        );
    });

    // #1349 — which loaded estimates can actually be emailed, and what the
    // current pick is worth. The dialog gets its own totals from the server;
    // this is just so the bar can carry a number without a round trip.
    const sendableEstimates = useMemo(
        () => (estimatesQ.data?.estimates ?? []).filter((r) => isSendable(r)),
        [estimatesQ.data],
    );
    const selectedEstimateTotal = useMemo(() => {
        const picked = new Set(selectedEstimates);
        return (estimatesQ.data?.estimates ?? [])
            .filter((r) => picked.has(r.id))
            .reduce((n, r) => n + Number(r.subtotal || 0), 0);
    }, [estimatesQ.data, selectedEstimates]);

    // A changed search or status filter means the visible set is no longer the
    // set that was picked. Keeping the selection would let the office send
    // estimates they can no longer see — so it clears.
    useEffect(() => {
        setSelectedEstimates([]);
    }, [debouncedEstimatesSearch, estimatesStatus]);

    const selectedContactCount = contactRows.filter((r) => selectedContacts.has(r.id)).length;

    const onRemoveSelectedContacts = async () => {
        const picked = contactRows.filter((r) => selectedContacts.has(r.id));
        if (picked.length === 0) return;
        const ok = await confirmDialog({
            title: picked.length === 1 ? "Remove this contact?" : `Remove ${picked.length} contacts?`,
            message: buildRemovalMessage({
                count: picked.length,
                importedCount: picked.filter((r) => r.fromServiceTitan).length,
                singleName:
                    picked.length === 1
                        ? `${picked[0]!.first_name} ${picked[0]!.last_name ?? ""}`.trim() || "this contact"
                        : undefined,
            }),
            destructive: true,
            focusCancel: true,
            confirmLabel: picked.length === 1 ? "Remove" : `Remove ${picked.length}`,
        });
        if (ok) removeContacts.mutate(picked.map((r) => r.id));
    };

    const filteredAgreements = ((agreementsQ.data?.agreements ?? []) as AgreementRow[]).filter((r) => {
        const q = agreementsSearch.trim().toLowerCase();
        if (!q) return true;
        return (
            (r.title ?? "").toLowerCase().includes(q) ||
            (r.status ?? "").toLowerCase().includes(q) ||
            (r.renewal_type ?? "").toLowerCase().includes(q) ||
            (r.billing_cycle ?? "").toLowerCase().includes(q)
        );
    });

    // ── Loading / 404 ───────────────────────────────────────
    if (loadingCustomer) {
        return <DetailSkeleton />;
    }
    if (!customer) {
        return (
            <DetailErrorState
                error={customerError}
                onRetry={() => void refetchCustomer()}
                notFoundTitle="Customer not found"
                listHref="/customers"
                listLabel="Back to customers"
            />
        );
    }

    // ── Derived stats ───────────────────────────────────────
    // The invoices list is paginated, so we don't reduce over it for totals —
    // Revenue Collected, Outstanding, and the unpaid count come from the
    // /customers/:id endpoint which aggregates server-side over ALL invoices.
    const totalRevenue = customer.revenueCollected ?? 0;
    const outstandingAmount = customer.outstandingAmount;

    // ── Column definitions ──────────────────────────────────
    const jobCols: Column<JobRow>[] = [
        {
            key: "id",
            header: "ID",
            className: "w-20",
            sortable: true,
            sortAccessor: (r) => r.display_number || r.id.slice(0, 8),
            render: (r) => (
                <Link
                    to={`/jobs/${r.id}`}
                    className="text-primary hover:underline font-mono text-xs"
                    onClick={(e) => e.stopPropagation()}
                >
                    #{r.display_number || r.id.slice(0, 8)}
                </Link>
            ),
        },
        { key: "title", header: "Title", sortable: true },
        {
            key: "site_name",
            header: "Location",
            sortable: true,
            render: (r) => <SiteCell siteId={r.site_id} siteName={r.site_name} city={r.site_city} />,
        },
        {
            key: "status",
            header: "Status",
            sortable: true,
            render: (r) => (
                <Badge className={statusColor(r.status)} variant="outline">
                    {r.status?.replace(/_/g, " ")}
                </Badge>
            ),
        },
        {
            key: "assigned_to_name",
            header: "Assigned To",
            sortable: true,
            render: (r) => <span className="text-sm">{r.assigned_to_name ?? "—"}</span>,
        },
        {
            key: "scheduled_start",
            header: "Scheduled",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.scheduled_start),
            render: (r) => (
                <span className="text-xs text-foreground/80">
                    {r.scheduled_start ? formatDate(r.scheduled_start) : "—"}
                </span>
            ),
        },
        {
            key: "created_at",
            header: "Created",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.created_at),
            render: (r) => <span className="text-xs text-foreground/80">{formatDate(r.created_at)}</span>,
        },
        openRowColumn<JobRow>(),
    ];

    const contactCols: Column<ContactDisplayRow>[] = [
        // Only present while editing. Select-all lives in the bar above the
        // table rather than this header, because DataTable's `header` is a
        // plain string — changing that shared component for one screen would
        // have been the bigger change.
        ...(contactsEditing
            ? [
                  {
                      key: "select",
                      header: "",
                      className: "w-8",
                      render: (r: ContactDisplayRow) => (
                          <Checkbox
                              data-testid={`contact-select-${r.id}`}
                              checked={selectedContacts.has(r.id)}
                              aria-label={`Select ${r.first_name} ${r.last_name ?? ""}`.trim()}
                              onCheckedChange={() =>
                                  setSelectedContacts((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(r.id)) next.delete(r.id);
                                      else next.add(r.id);
                                      return next;
                                  })
                              }
                          />
                      ),
                  } satisfies Column<ContactDisplayRow>,
              ]
            : []),
        {
            key: "first_name",
            header: "Name",
            sortable: true,
            sortAccessor: (r) => contactNameDisplay(r.first_name, r.last_name).text,
            render: (r) => (
                <div className="flex items-center gap-2">
                    {/* #824's fix reached only the job page; the umbrella row
                        read as a person here. One helper, no fifth dialect. */}
                    <span
                        className={cn(
                            "font-medium",
                            contactNameDisplay(r.first_name, r.last_name).isUmbrella &&
                                "text-amber-600 dark:text-amber-400",
                        )}
                    >
                        {contactNameDisplay(r.first_name, r.last_name).text}
                    </span>
                    {r.is_primary && (
                        <Badge variant="success" className="text-[10px] py-0 px-1.5 gap-0.5">
                            <Star className="h-3 w-3" /> Primary
                        </Badge>
                    )}
                    {r.fromServiceTitan && (
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                            imported
                        </Badge>
                    )}
                </div>
            ),
        },
        {
            key: "role_title",
            header: "Role",
            sortable: true,
            render: (r) => <span className="text-sm">{r.role_title ?? "—"}</span>,
        },
        {
            key: "methods",
            header: "Contact methods",
            render: (r) =>
                r.methods.length === 0 ? (
                    <span className="text-foreground/80 text-sm">—</span>
                ) : (
                    <div className="space-y-0.5">
                        {r.methods.map((m) => (
                            <div key={m.id} className="space-y-0.5">
                                <div className="flex items-center gap-1.5 text-sm">
                                    {m.kind === "email" ? (
                                        <Mail className="h-3 w-3 shrink-0 text-foreground/60" />
                                    ) : (
                                        <Phone className="h-3 w-3 shrink-0 text-foreground/60" />
                                    )}
                                    {m.kind === "email" ? (
                                        <a
                                            href={`mailto:${m.value}`}
                                            className="text-primary hover:underline break-all"
                                        >
                                            {m.value}
                                        </a>
                                    ) : (
                                        <a href={`tel:${m.value}`} className="truncate">
                                            {m.value}
                                            {m.kind === "fax" && (
                                                <span className="ml-1 text-xs text-foreground/60">(fax)</span>
                                            )}
                                        </a>
                                    )}
                                    {(m.memo || m.label) && (
                                        <span className="text-xs text-foreground/60 truncate">{m.memo || m.label}</span>
                                    )}
                                    {m.fromServiceTitan && (
                                        <Badge
                                            variant="secondary"
                                            className="text-[9px] py-0 px-1 shrink-0"
                                            title="Imported"
                                        >
                                            ST
                                        </Badge>
                                    )}
                                </div>
                                {m.purposes.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-1 pl-[18px]">
                                        {m.purposes.map((p) => (
                                            <span
                                                key={p}
                                                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-1.5 py-0 text-[10px] text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200"
                                                title={`Always receives ${(
                                                    PURPOSE_LABEL[p] ?? p
                                                ).toLowerCase()} — saved from Approve & Send ("remember routing"). Remove to stop.`}
                                            >
                                                {PURPOSE_LABEL[p] ?? p}
                                                <button
                                                    type="button"
                                                    className="text-emerald-700/70 hover:text-red-600 dark:text-emerald-300/70"
                                                    aria-label={`Stop always sending ${PURPOSE_LABEL[p] ?? p} to ${m.value}`}
                                                    disabled={removePurpose.isPending}
                                                    onClick={() =>
                                                        removePurpose.mutate({ methodId: m.id, purposeKey: p })
                                                    }
                                                >
                                                    <X className="h-2.5 w-2.5" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ),
        },
        {
            key: "last_verified_at",
            header: "Verified",
            render: (r) => {
                const stale = isContactStale(r.last_verified_at);
                return (
                    <span
                        className={cn("text-xs", stale ? "text-amber-600 dark:text-amber-400" : "text-foreground/70")}
                        title={r.last_verified_by_name ? `by ${r.last_verified_by_name}` : undefined}
                    >
                        {formatVerifiedLabel(r.last_verified_at)}
                    </span>
                );
            },
        },
        {
            key: "id",
            header: "",
            // Verify and edit still go through the legacy customer_contacts
            // CRUD, so they need the linked legacy row and stay disabled on
            // an imported contact. REMOVE no longer does (#1307): it goes
            // through the v2 bulk endpoint, which is keyed on contacts.id and
            // tombstones the ServiceTitan record so the sync can't undo it.
            render: (r) => {
                const can = contactActionAvailability(r);
                return (
                    <div className="flex items-center justify-end gap-1">
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            disabled={verifyContact.isPending || !can.canVerify}
                            onClick={() => r.legacyId && verifyContact.mutate(r.legacyId)}
                            aria-label="Confirm contact still current"
                            title={can.canVerify ? "Confirm Still Current" : "Imported"}
                        >
                            <ShieldCheck className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            disabled={!can.canEdit}
                            onClick={() => setEditingContact(r)}
                            aria-label="Edit contact"
                            title={can.canEdit ? "Edit contact" : "Imported"}
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            data-testid={`contact-remove-${r.id}`}
                            disabled={removeContacts.isPending}
                            onClick={async () => {
                                if (
                                    await confirmDialog({
                                        title: "Remove this contact?",
                                        message: buildRemovalMessage({
                                            count: 1,
                                            importedCount: r.fromServiceTitan ? 1 : 0,
                                            singleName: `${r.first_name} ${r.last_name ?? ""}`.trim() || "this contact",
                                        }),
                                        destructive: true,
                                        focusCancel: true,
                                        confirmLabel: "Remove",
                                    })
                                ) {
                                    removeContacts.mutate([r.id]);
                                }
                            }}
                            aria-label="Remove contact"
                            title={
                                r.fromServiceTitan
                                    ? "Remove this imported contact — it stays removed, and the sync won't put it back"
                                    : "Remove contact"
                            }
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                );
            },
        },
    ];

    const agreementCols: Column<AgreementRow>[] = [
        {
            key: "title",
            header: "Title",
            sortable: true,
            render: (r) => <span className="font-medium">{r.title}</span>,
        },
        {
            key: "status",
            header: "Status",
            sortable: true,
            render: (r) => (
                <Badge className={statusColor(r.status)} variant="outline">
                    {r.status}
                </Badge>
            ),
        },
        {
            key: "billing_cycle",
            header: "Billing",
            sortable: true,
            render: (r) => <span className="text-sm capitalize">{r.billing_cycle?.replace(/_/g, " ")}</span>,
        },
        {
            key: "amount",
            header: "Amount",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => moneySortValue(r.amount),
            render: (r) => (
                <span className="font-medium tabular-nums">{r.amount ? formatDollars(Number(r.amount)) : "—"}</span>
            ),
        },
        {
            key: "start_date",
            header: "Period",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.start_date),
            render: (r) => (
                <span className="text-xs text-foreground/80">
                    {formatDate(r.start_date)}
                    {r.end_date ? ` – ${formatDate(r.end_date)}` : " – ongoing"}
                </span>
            ),
        },
        {
            key: "renewal_type",
            header: "Renewal",
            sortable: true,
            render: (r) => <span className="text-sm capitalize">{r.renewal_type}</span>,
        },
    ];

    // ── Tab counts ──────────────────────────────────────────
    /**
     * `suffix` labels a count that is NOT the total. The Invoices tab silently
     * defaults to the Unpaid filter, so its badge was reporting "107" while the
     * customer has far more invoices than that — a number nobody could
     * reconcile. Say what it counts.
     */
    const tabBadge = (count: number | undefined, tabValue?: string, suffix?: string) => {
        if (count === undefined || count <= 0) return null;
        // On the active tab the trigger background is gold (primary), so the
        // default gold-on-gold badge is unreadable (~1:1). The first fix for
        // this (USABILITY_AUDIT_2 M4.1) used `bg-background/20 text-foreground`
        // and was never measured: a 20% wash over gold is still gold, so it
        // landed at 4.28 in light and 2.69 in DARK — the mode M4.1 was filed
        // against. The badge text is 10px, so the bar is 4.5, not 3.
        //
        // Invert the tab's own pair instead of washing it. It still reads as
        // "selected" (same two colours, swapped), adds no new token, and is
        // measured: 6.24 light (#7a5c0a on #ffffff), 8.69 dark (#d4a53a on
        // #0c0a09). The inactive badge is unchanged — 4.72 / 6.04 on
        // bg-secondary, both already passing.
        const isActive = tabValue !== undefined && activeTab === tabValue;
        return (
            <span
                className={cn(
                    "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                    isActive ? "bg-primary-foreground text-primary" : "bg-primary/10 text-primary",
                )}
            >
                {count}
                {suffix ? <span className="ml-0.5 font-normal opacity-80">{suffix}</span> : null}
            </span>
        );
    };

    // ── Render ──────────────────────────────────────────────
    return (
        <div className="space-y-6">
            {handoff && (
                <IntakeHandoffBanner
                    customerId={customer.id}
                    customerName={customer.company_name}
                    siteId={handoff.siteId}
                    intakeNotes={handoff.intakeNotes}
                    similar={handoff.similar}
                    onDismiss={() => setHandoff(null)}
                />
            )}

            {/* ── Service hold ("Do Not Service") ────────── */}
            <ServiceHoldBanner customer={customer} />

            {/* ── Breadcrumb + Header ─────────────────────── */}
            <div>
                <BackLink
                    fallback="/customers"
                    fallbackLabel="Back to customers"
                    className="text-sm text-shell-foreground/80 hover:text-shell-foreground inline-flex items-center gap-1 mb-2"
                    icon={<ArrowLeft className="h-3 w-3" />}
                />
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-black tracking-tight text-shell-foreground">
                                {customer.company_name}
                            </h1>
                            <Badge variant={customer.is_active ? "success" : "secondary"}>
                                {customer.is_active ? "Active" : "Inactive"}
                            </Badge>
                            <BudgetChip customerId={customer.id} />
                            {customer.service_hold && (
                                <Badge variant="destructive" className="gap-1">
                                    <AlertTriangle className="h-3 w-3" /> Do Not Service
                                </Badge>
                            )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-shell-foreground/80">
                            <CustomerTypeHeaderPick customerId={customer.id} customerType={customer.customer_type} />
                            <span>Added {formatDate(customer.created_at)}</span>
                            <CameFromChip subject={customer} />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <TaskCreateButton
                            prefill={{
                                title: `${customer.company_name} — `,
                                links: [
                                    {
                                        entityType: "customer",
                                        entityId: customer.id,
                                        label: customer.company_name,
                                    },
                                ],
                            }}
                        />
                        <Link
                            to={`/customers/${customer.id}/edit`}
                            className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                        >
                            <Pencil className="h-4 w-4 mr-1.5" /> Edit
                        </Link>
                        <NewJobButton customerId={customer.id} />
                        <ServiceHoldButton customer={customer} />
                    </div>
                </div>
            </div>

            {/* ── Open tasks linked to this customer ─────── */}
            <TaskListInline entityType="customer" entityId={customer.id} />

            {/* ── Add from the field: notes techs added for this customer ── */}
            <FromTheFieldSection kind="customer" id={customer.id} />

            {/* ── Service Summary ────────────────────────── */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Service Summary</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {/* #992 — "Devices due (90 days)" used to fold PAST-due
                            devices in with upcoming ones, so 40 overdue and 40
                            coming up read identically. Overdue leads, because
                            it is already a problem, and each half deep-links to
                            the Locations bucket that lists the sites. */}
                        <div className="flex items-start gap-2">
                            <AlertTriangle
                                className={cn(
                                    "h-4 w-4 mt-0.5 shrink-0",
                                    (customer.devicesOverdue ?? 0) > 0
                                        ? "text-destructive"
                                        : (customer.devicesDueSoon ?? 0) > 0
                                          ? "text-amber-500"
                                          : "text-muted-foreground",
                                )}
                            />
                            <div>
                                <div className="text-xs text-muted-foreground">Devices</div>
                                <div className="flex items-baseline gap-2">
                                    <button
                                        type="button"
                                        onClick={() => goToLocationsBucket("overdue")}
                                        className="text-lg font-semibold tabular-nums hover:underline"
                                        title="Show the locations with overdue devices"
                                    >
                                        {customer.devicesOverdue ?? 0}{" "}
                                        <span className="text-xs font-normal text-muted-foreground">overdue</span>
                                    </button>
                                    <span className="text-muted-foreground">·</span>
                                    <button
                                        type="button"
                                        onClick={() => goToLocationsBucket("all")}
                                        className="text-lg font-semibold tabular-nums hover:underline"
                                        title="Show all locations"
                                    >
                                        {customer.devicesDueSoon ?? 0}{" "}
                                        <span className="text-xs font-normal text-muted-foreground">due in 90d</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-2">
                            <Activity className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                            <div>
                                <div className="text-xs text-muted-foreground">Last test / inspection</div>
                                <div className="text-lg font-semibold">
                                    {customer.lastTestAt ? formatDate(customer.lastTestAt) : "—"}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-2">
                            <CalendarPlus className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                            <div>
                                <div className="text-xs text-muted-foreground">Next scheduled visit</div>
                                <div className="text-lg font-semibold">
                                    {customer.nextScheduledVisit ? formatDate(customer.nextScheduledVisit) : "—"}
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Stat Cards ─────────────────────────────── */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* #992 — "open jobs" was printed three times in this header
                    (Service Summary, this card's description, and the Open Jobs
                    card). Repeating a number does not make it clearer; it makes
                    the reader check whether the three agree. Stated once. */}
                <StatCard
                    title="Total Jobs"
                    value={customer.jobCount}
                    icon={<Briefcase className="h-4 w-4" />}
                    description={`${customer.locationCount} ${customer.locationCount === 1 ? "location" : "locations"}`}
                />
                <StatCard
                    title="Open Jobs"
                    value={customer.openJobCount}
                    icon={<ClipboardList className="h-4 w-4" />}
                />
                {canFinancials && (
                    <StatCard
                        title="Revenue Collected"
                        value={formatDollars(totalRevenue)}
                        icon={<DollarSign className="h-4 w-4" />}
                    />
                )}
                {/* #894 — the Statement button lives beside the number it
                    explains. Same card body as StatCard, plus the action. */}
                <Card className="hover:shadow-[var(--shadow-card-hover)]">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wide text-foreground/80">
                                Outstanding
                            </p>
                            <Receipt className="h-4 w-4 text-foreground/80" />
                        </div>
                        <div className="mt-3 flex items-end justify-between gap-2">
                            <div>
                                <p className="text-3xl font-bold tracking-tight tabular-nums">
                                    {formatDollars(outstandingAmount)}
                                </p>
                                <p className="mt-1.5 text-xs text-foreground/80">
                                    {customer.unpaidInvoiceCount} unpaid invoices
                                </p>
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={outstandingAmount <= 0}
                                onClick={() => setStatementOpen(true)}
                                data-testid="customer-statement-button"
                            >
                                <FileText className="mr-1 h-3.5 w-3.5" /> Statement
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
            {statementOpen && (
                <StatementDialog
                    customerId={customer.id}
                    customerName={customer.company_name}
                    open={statementOpen}
                    onClose={() => setStatementOpen(false)}
                />
            )}

            {/* ── Tabs ───────────────────────────────────── */}
            <Tabs
                value={activeTab}
                onValueChange={(tab) =>
                    setSearchParams((p) => {
                        p.set("tab", tab);
                        return p;
                    })
                }
                className="space-y-4"
            >
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="overview">
                        <Building className="h-3.5 w-3.5 mr-1.5" /> Overview
                    </TabsTrigger>
                    <TabsTrigger value="locations">
                        <MapPin className="h-3.5 w-3.5 mr-1.5" /> Locations
                        {tabBadge(customer.locationCount, "locations")}
                    </TabsTrigger>
                    <TabsTrigger value="jobs">
                        <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Jobs
                        {tabBadge(jobsQ.data?.total, "jobs")}
                    </TabsTrigger>
                    <TabsTrigger value="work-orders">
                        <FileStack className="h-3.5 w-3.5 mr-1.5" /> Work Order Forms
                    </TabsTrigger>
                    <TabsTrigger value="estimates">
                        <FileText className="h-3.5 w-3.5 mr-1.5" /> Estimates
                        {tabBadge(estimatesQ.data?.total, "estimates")}
                    </TabsTrigger>
                    <TabsTrigger value="invoices">
                        <Receipt className="h-3.5 w-3.5 mr-1.5" /> Invoices
                        {tabBadge(
                            invoicesQ.data?.total,
                            "invoices",
                            invoicesStatus === "unpaid" ? " unpaid" : undefined,
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="contacts">
                        <Users className="h-3.5 w-3.5 mr-1.5" /> Contacts
                        {tabBadge(contactRows.length, "contacts")}
                    </TabsTrigger>
                    {canCrmManage && (
                        <TabsTrigger value="routing">
                            <Network className="h-3.5 w-3.5 mr-1.5" /> Contacts &amp; Routing
                        </TabsTrigger>
                    )}
                    {canSeeMoney && (
                        <TabsTrigger value="budget">
                            <PiggyBank className="h-3.5 w-3.5 mr-1.5" /> Billing &amp; budget
                        </TabsTrigger>
                    )}
                    {showRules && (
                        <TabsTrigger value="rules">
                            <ListChecks className="h-3.5 w-3.5 mr-1.5" /> Rules
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="agreements">
                        <ScrollText className="h-3.5 w-3.5 mr-1.5" /> Agreements
                        {tabBadge(agreementsQ.data?.agreements?.length, "agreements")}
                    </TabsTrigger>
                    {isAdmin && (
                        <TabsTrigger value="billing">
                            <Wallet className="h-3.5 w-3.5 mr-1.5" /> Rate Card
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="calls">
                        <Phone className="h-3.5 w-3.5 mr-1.5" /> Calls
                    </TabsTrigger>
                    <TabsTrigger value="activity">
                        <Activity className="h-3.5 w-3.5 mr-1.5" /> Activity
                    </TabsTrigger>
                </TabsList>

                {/* ── Overview Tab ────────────────────────── */}
                <TabsContent value="overview">
                    <div className="grid gap-4 lg:grid-cols-3">
                        {/* Info Card */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Building className="h-4 w-4" /> Company Info
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                <InfoRow label="Name" value={customer.company_name} />
                                <InfoRow
                                    label="Home or business"
                                    value={customerTypeWord(customer.customer_type) ?? "Not picked yet"}
                                />
                                <InfoRow label="Status" value={customer.is_active ? "Active" : "Inactive"} />
                                <InfoRow label="Created" value={formatDate(customer.created_at)} />
                                <InfoRow label="Updated" value={formatDateTime(customer.updated_at)} />
                                <InfoRow label="Locations" value={String(customer.locationCount)} />
                                {customer.payment_profile && (
                                    <InfoRow
                                        label="Payment Profile"
                                        value={
                                            customer.payment_profile === "net_terms" && customer.net_terms_days
                                                ? `Net terms (${customer.net_terms_days} ${pluralize(customer.net_terms_days, "day")})`
                                                : customer.payment_profile.replace(/_/g, " ")
                                        }
                                        className="capitalize"
                                    />
                                )}
                                {/* #887 — the WO close-out gate, flipped per account.
                                    Maintenance companies (Cintas etc.) pay against
                                    their own WO number; with this on, jobs can't be
                                    completed until one is on the job. */}
                                <RequiresWoToggleRow
                                    customerId={customer.id}
                                    value={customer.requires_customer_wo === true}
                                />
                                {/* #905 — the reminder machine's per-account
                                    switch: hand-managed accounts (the top-ten
                                    treatment) never get an automatic email. */}
                                <CollectionsModeRow
                                    customerId={customer.id}
                                    value={customer.collections_mode === "hand_managed" ? "hand_managed" : "auto"}
                                />
                                {/* TAX VISIBILITY: the tax chip lives on the customer page. */}
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-foreground/70">Sales tax</span>
                                    <span
                                        title={
                                            customer.tax_status === "exempt" && customer.tax_exempt_reason
                                                ? customer.tax_exempt_reason
                                                : undefined
                                        }
                                        className={
                                            customer.tax_status === "taxable"
                                                ? "inline-flex items-center rounded-full border border-emerald-400/60 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                                                : customer.tax_status === "exempt"
                                                  ? "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-foreground/80"
                                                  : "inline-flex items-center rounded-full border border-amber-400/70 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                                        }
                                    >
                                        {customer.tax_status === "taxable"
                                            ? "Taxable"
                                            : customer.tax_status === "exempt"
                                              ? "Exempt"
                                              : "Tax status not set"}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        <Card>
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <ScrollText className="h-4 w-4" /> Notes
                                </CardTitle>
                                {!notesEditing && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            setNotesDraft(customer.notes ?? "");
                                            setNotesEditing(true);
                                        }}
                                    >
                                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                                    </Button>
                                )}
                            </CardHeader>
                            <CardContent className="text-sm">
                                {notesEditing ? (
                                    <div className="space-y-2">
                                        <Textarea
                                            value={notesDraft}
                                            onChange={(e) => setNotesDraft(e.target.value)}
                                            rows={5}
                                            placeholder="Internal notes about this customer…"
                                        />
                                        <div className="flex justify-end gap-2">
                                            <Button size="sm" variant="outline" onClick={() => setNotesEditing(false)}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                disabled={saveNotes.isPending}
                                                onClick={() => saveNotes.mutate()}
                                            >
                                                {saveNotes.isPending ? "Saving…" : "Save"}
                                            </Button>
                                        </div>
                                    </div>
                                ) : customer.notes ? (
                                    <p className="whitespace-pre-wrap text-foreground/90">{customer.notes}</p>
                                ) : (
                                    <p className="text-foreground/60">No notes yet.</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Card on File */}
                        {id && (
                            <CardOnFilePanel
                                customerId={id}
                                hasCard={customer.has_card_on_file}
                                brand={customer.stripe_payment_method_brand}
                                last4={customer.stripe_payment_method_last4}
                            />
                        )}

                        {/* Priority & Scheduling */}
                        {id && (
                            <PriorityTierPanel
                                customerId={id}
                                tier={customer.priority_tier ?? null}
                                monthlyOverride={customer.monthly_batch_target_override ?? null}
                            />
                        )}

                        {/* Recent Jobs */}
                        <Card className="lg:col-span-2">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Briefcase className="h-4 w-4" /> Recent Jobs
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <DataTable
                                    columns={jobCols.filter((c) =>
                                        ["id", "title", "status", "created_at", "__open"].includes(c.key),
                                    )}
                                    data={(jobsQ.data?.jobs ?? []).slice(0, 5)}
                                    keyField="id"
                                    onRowClick={(r) => navigate(`/jobs/${r.id}`)}
                                    loading={jobsQ.isLoading}
                                    emptyMessage="No jobs for this customer yet."
                                />
                            </CardContent>
                        </Card>

                        {/* Primary Contacts */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Users className="h-4 w-4" /> Contacts
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {contactsQ.isLoading ? (
                                    <div className="space-y-2">
                                        {Array.from({ length: 2 }).map((_, i) => (
                                            <Skeleton key={i} className="h-10 w-full" />
                                        ))}
                                    </div>
                                ) : contactRows.length === 0 ? (
                                    <p className="text-sm text-foreground/80">No contacts</p>
                                ) : (
                                    contactRows.slice(0, 4).map((c) => (
                                        <div key={c.id} className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold shrink-0">
                                                {c.first_name[0]}
                                                {c.last_name?.[0] ?? ""}
                                            </div>
                                            <div className="min-w-0">
                                                <p
                                                    className={cn(
                                                        "text-sm font-medium truncate",
                                                        contactNameDisplay(c.first_name, c.last_name).isUmbrella &&
                                                            "text-amber-600 dark:text-amber-400",
                                                    )}
                                                >
                                                    {contactNameDisplay(c.first_name, c.last_name).text}
                                                    {c.is_primary && (
                                                        <Star className="inline h-3 w-3 ml-1 text-amber-500" />
                                                    )}
                                                </p>
                                                <div className="flex items-center gap-2 text-xs text-foreground/80">
                                                    {c.email && (
                                                        <span className="flex items-center gap-0.5 truncate">
                                                            <Mail className="h-3 w-3 shrink-0" /> {c.email}
                                                        </span>
                                                    )}
                                                    {c.phone && (
                                                        <span className="flex items-center gap-0.5">
                                                            <Phone className="h-3 w-3 shrink-0" /> {c.phone}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </CardContent>
                        </Card>

                        {/* Customer Portal Access */}
                        {id && <CustomerPortalAccessPanel customerId={id} />}

                        {/* AI Insight */}
                        {id && <AiCustomerInsight customerId={id} />}

                        {/* Recent Activity */}
                        <Card className="lg:col-span-2">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Activity className="h-4 w-4" /> Recent Activity
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {id && <ActivityTimeline entityType="customer" entityId={id} limit={8} />}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* ── Locations Tab ───────────────────────── */}
                <TabsContent value="locations">
                    <div className="space-y-4">
                        {id && <LocationsTabPanel customerId={id} />}
                        {/* Guided interviews: brand-routing collection walkthroughs
                            live with the locations they describe. */}
                        {id && <InterviewsCard customerId={id} />}
                    </div>
                </TabsContent>

                {/* ── Work Orders Tab ─────────────────────── */}
                <TabsContent value="work-orders">{id && <WorkOrdersTabPanel customerId={id} />}</TabsContent>

                {/* ── Jobs Tab ────────────────────────────── */}
                <TabsContent value="jobs">
                    <Card>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Jobs ({jobsQ.data?.total ?? 0})</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <TabToolbar
                                searchValue={jobsSearch}
                                onSearchChange={setJobsSearch}
                                searchPlaceholder="Search title, job #, or location — e.g. “Petsmart Hurst”"
                                statusValue={jobsStatus}
                                onStatusChange={setJobsStatus}
                                statusOptions={JOB_STATUS_OPTIONS}
                            />
                            <DataTable
                                columns={jobCols}
                                data={jobsQ.data?.jobs ?? []}
                                keyField="id"
                                onRowClick={(r) => navigate(`/jobs/${r.id}`)}
                                sortId="customer.jobs"
                                loading={jobsQ.isLoading}
                                emptyMessage={
                                    jobsSearch || jobsStatus
                                        ? "No jobs match this search or filter. Clear them to see the whole history."
                                        : "No jobs for this customer yet."
                                }
                            />
                            <SortScopeNote
                                loaded={jobsQ.data?.jobs.length ?? 0}
                                total={jobsQ.data?.total}
                                noun="jobs"
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Estimates Tab ───────────────────────── */}
                <TabsContent value="estimates">
                    <Card>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Estimates ({estimatesQ.data?.total ?? 0})</CardTitle>
                            <div className="flex items-center gap-2">
                                {/* #1349 — Select turns the checkbox column on.
                                    Off by default so the tab reads the same as
                                    it always has until someone asks to send. */}
                                <Button
                                    size="sm"
                                    variant={estimateSelectMode ? "secondary" : "outline"}
                                    data-testid="estimates-select-toggle"
                                    onClick={() => {
                                        setEstimateSelectMode((on) => !on);
                                        setSelectedEstimates([]);
                                    }}
                                >
                                    <CheckSquare className="mr-1 h-3.5 w-3.5" />
                                    {estimateSelectMode ? "Done selecting" : "Select"}
                                </Button>
                                {estimateSelectMode && selectedEstimates.length > 0 && (
                                    <Button size="sm" onClick={() => setEstimateSendOpen(true)}>
                                        <Mail className="mr-1 h-3.5 w-3.5" /> Email {selectedEstimates.length} estimate
                                        {selectedEstimates.length === 1 ? "" : "s"}
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <TabToolbar
                                searchValue={estimatesSearch}
                                onSearchChange={setEstimatesSearch}
                                searchPlaceholder="Search title, estimate #, notes, or location…"
                                statusValue={estimatesStatus}
                                onStatusChange={setEstimatesStatus}
                                statusOptions={ESTIMATE_STATUS_OPTIONS}
                            />
                            {estimateSelectMode && (
                                <EstimateSelectAllBar
                                    sendableCount={sendableEstimates.length}
                                    blockedCount={(estimatesQ.data?.estimates.length ?? 0) - sendableEstimates.length}
                                    selectedCount={selectedEstimates.length}
                                    allSelected={
                                        sendableEstimates.length > 0 &&
                                        selectedEstimates.length === sendableEstimates.length
                                    }
                                    onSelectAll={(checked) =>
                                        setSelectedEstimates(checked ? sendableEstimates.map((r) => r.id) : [])
                                    }
                                    onClear={() => setSelectedEstimates([])}
                                />
                            )}
                            <DataTable
                                columns={estimateCols}
                                data={estimatesQ.data?.estimates ?? []}
                                keyField="id"
                                sortId="customer.estimates"
                                loading={estimatesQ.isLoading}
                                onRowClick={(r) => navigate(`/commercial/${r.id}?type=estimate`)}
                                // #1349 — pick several quotes and send them as
                                // one zip. A declined or cancelled row keeps its
                                // place with the checkbox disabled, so "why
                                // isn't that one in my batch?" is answered by
                                // the row itself.
                                selectable={estimateSelectMode}
                                selectedIds={selectedEstimates}
                                onSelectionChange={(ids) => setSelectedEstimates(ids.map(String))}
                                rowSelectable={(r) => isSendable(r)}
                                rowUnselectableReason={(r) => unsendableReason(r)}
                                totalRows={estimatesQ.data?.total}
                                emptyMessage={
                                    estimatesSearch || estimatesStatus
                                        ? "No estimates match this search or filter. Clear them to see all estimates."
                                        : "No estimates for this customer yet."
                                }
                            />
                            {estimateSelectMode && (
                                <EstimateBatchBar
                                    selectedCount={selectedEstimates.length}
                                    selectedTotal={selectedEstimateTotal}
                                    scopeLabel={estimateScopeLabel(
                                        estimatesQ.data?.estimates.length ?? 0,
                                        estimatesQ.data?.total,
                                        sendableEstimates.length,
                                    )}
                                    onClear={() => setSelectedEstimates([])}
                                    onEmail={() => setEstimateSendOpen(true)}
                                />
                            )}
                            <EstimateSendDialog
                                customerId={id!}
                                customerName={customer?.company_name ?? null}
                                estimateIds={selectedEstimates}
                                open={estimateSendOpen}
                                onClose={() => setEstimateSendOpen(false)}
                                onSent={() => {
                                    setSelectedEstimates([]);
                                    setEstimateSelectMode(false);
                                    void estimatesQ.refetch();
                                }}
                            />
                            <SortScopeNote
                                loaded={estimatesQ.data?.estimates.length ?? 0}
                                total={estimatesQ.data?.total}
                                noun="estimates"
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Invoices Tab ────────────────────────── */}
                <TabsContent value="invoices">
                    <Card>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Invoices ({invoicesQ.data?.total ?? 0})</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {/* #1812 — "what invoices did check #1845 pay?" */}
                            <div className="mb-4 rounded-lg border border-dashed bg-muted/30 p-3">
                                <p className="mb-2 text-sm font-semibold text-foreground">
                                    Find a payment — which invoices did a check pay?
                                </p>
                                <PaymentLookup customerId={id!} />
                            </div>
                            {/* #1916 — one check, many jobs: record it, credits, same payer, receipt. */}
                            <CustomerPaymentStrip customerId={id!} selectedInvoiceIds={selectedInvoices} />
                            <TabToolbar
                                searchValue={invoicesSearch}
                                onSearchChange={setInvoicesSearch}
                                searchPlaceholder="Search invoice # or title…"
                                statusValue={invoicesStatus}
                                onStatusChange={setInvoicesStatus}
                                statusOptions={INVOICE_STATUS_OPTIONS}
                            />
                            <DataTable
                                columns={invoiceCols}
                                data={invoicesQ.data?.invoices ?? []}
                                keyField="id"
                                sortId="customer.invoices"
                                loading={invoicesQ.isLoading}
                                onRowClick={(r) => navigate(`/invoices/${r.id}`)}
                                // #1345 — pick several invoices and settle them
                                // with ONE charge to one card. A paid or void
                                // row keeps its place with the checkbox
                                // disabled, so "why isn't that one in my
                                // batch?" is answered by the row itself.
                                selectable
                                selectedIds={selectedInvoices}
                                onSelectionChange={(ids) => setSelectedInvoices(ids.map(String))}
                                rowSelectable={(r) => isChargeable(r)}
                                rowUnselectableReason={(r) => unselectableReason(r)}
                                totalRows={invoicesQ.data?.total}
                                emptyMessage={
                                    invoicesSearch || invoicesStatus
                                        ? invoicesStatus === "unpaid" && !invoicesSearch
                                            ? "Nothing unpaid — this account is settled. Switch the filter to see paid invoices."
                                            : "No invoices match this search or filter. Clear them to see all invoices."
                                        : "No invoices for this customer yet."
                                }
                            />
                            <BatchChargeBar
                                customerId={id!}
                                customerName={customer?.company_name ?? null}
                                selectedIds={selectedInvoices}
                                rows={invoicesQ.data?.invoices ?? []}
                                scopeLabel={selectionScopeLabel(
                                    invoicesQ.data?.invoices.length ?? 0,
                                    invoicesQ.data?.total,
                                    (invoicesQ.data?.invoices ?? []).filter(isChargeable).length,
                                )}
                                onClear={() => setSelectedInvoices([])}
                                onDone={() => {
                                    void invoicesQ.refetch();
                                }}
                            />
                            <SortScopeNote
                                loaded={invoicesQ.data?.invoices.length ?? 0}
                                total={invoicesQ.data?.total}
                                noun="invoices"
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Contacts Tab ────────────────────────── */}
                <TabsContent value="contacts">
                    <Card>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Contacts ({contactRows.length})</CardTitle>
                            <div className="flex items-center gap-2">
                                {contactRows.length > 0 && (
                                    <Button
                                        size="sm"
                                        variant={contactsEditing ? "secondary" : "ghost"}
                                        data-testid="contacts-edit-toggle"
                                        aria-pressed={contactsEditing}
                                        onClick={() => {
                                            setContactsEditing((on) => !on);
                                            setSelectedContacts(new Set());
                                        }}
                                    >
                                        <Pencil className="h-4 w-4 mr-1.5" />
                                        {contactsEditing ? "Done" : "Edit"}
                                    </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => setAddContactOpen(true)}>
                                    <UserPlus className="h-4 w-4 mr-1.5" /> Add Contact
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <TabToolbar
                                searchValue={contactsSearch}
                                onSearchChange={setContactsSearch}
                                searchPlaceholder="Search name, email, phone, or role…"
                            />
                            {contactsEditing && (
                                <div
                                    data-testid="contacts-select-bar"
                                    className="mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-xs"
                                >
                                    <label className="flex cursor-pointer items-center gap-2 font-medium">
                                        <Checkbox
                                            checked={
                                                filteredContacts.length > 0 &&
                                                filteredContacts.every((r) => selectedContacts.has(r.id))
                                            }
                                            aria-label="Select everyone shown"
                                            onCheckedChange={(v) =>
                                                setSelectedContacts(
                                                    v === true ? new Set(filteredContacts.map((r) => r.id)) : new Set(),
                                                )
                                            }
                                        />
                                        {/* Deliberately "shown", not "all": with a search
                                            active this ticks the filtered rows, and saying
                                            "all" would be a lie the office pays for. */}
                                        Select all {filteredContacts.length} shown
                                        {contactsSearch.trim() ? " (matching your search)" : ""}
                                    </label>
                                    <span className="text-foreground/70">
                                        {selectedContactCount === 0
                                            ? "Tick the contacts to remove"
                                            : `${selectedContactCount} selected`}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        className="ml-auto h-7"
                                        data-testid="contacts-remove-selected"
                                        disabled={selectedContactCount === 0 || removeContacts.isPending}
                                        onClick={() => void onRemoveSelectedContacts()}
                                    >
                                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                        {removeContacts.isPending
                                            ? removalProgress
                                                ? `Removing ${removalProgress.done} of ${removalProgress.total}…`
                                                : "Removing…"
                                            : `Remove ${selectedContactCount > 0 ? selectedContactCount : ""}`}
                                    </Button>
                                </div>
                            )}
                            <DataTable
                                columns={contactCols}
                                data={filteredContacts}
                                keyField="id"
                                sortId="customer.contacts"
                                loading={contactsQ.isLoading}
                                emptyMessage={
                                    contactsSearch
                                        ? "No contacts match this search."
                                        : "No contacts for this customer yet."
                                }
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Contacts & Routing Tab (Prompt C) ───── */}
                {canCrmManage && (
                    <TabsContent value="routing">
                        {id && <ContactsRoutingTab customerId={id} customerName={customer.company_name} />}
                    </TabsContent>
                )}

                {/* ── Billing & budget (threads/customer-billing-budget) ── */}
                {canSeeMoney && (
                    <TabsContent value="budget">
                        <BillingBudgetTab customerId={customer.id} />
                    </TabsContent>
                )}

                {/* ── Rules (Teach it once, #1738) ─────────── */}
                {showRules && (
                    <TabsContent value="rules">
                        {id && <CustomerRulesTab customerId={id} customerName={customer.company_name} />}
                    </TabsContent>
                )}

                {/* ── Agreements Tab ──────────────────────── */}
                <TabsContent value="agreements">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">
                                Service Agreements ({agreementsQ.data?.agreements?.length ?? 0})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <TabToolbar
                                searchValue={agreementsSearch}
                                onSearchChange={setAgreementsSearch}
                                searchPlaceholder="Search title, status, or terms…"
                            />
                            <DataTable
                                columns={agreementCols}
                                data={filteredAgreements}
                                keyField="id"
                                sortId="customer.agreements"
                                loading={agreementsQ.isLoading}
                                emptyMessage={
                                    agreementsSearch
                                        ? "No agreements match this search."
                                        : "No service agreements for this customer yet."
                                }
                            />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Rate Card Tab ─────────────────────────── */}
                {isAdmin && (
                    <TabsContent value="billing">
                        <CustomerBillingPanel
                            customerId={customer.id}
                            customerName={customer.company_name}
                            rateCardId={rateCardId}
                        />
                    </TabsContent>
                )}

                {/* ── Calls Tab ─────────────────────────── */}
                <TabsContent value="calls">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Phone className="h-5 w-5" /> Call Log
                            </CardTitle>
                        </CardHeader>
                        <CardContent>{id && <CallLogList customerId={id} />}</CardContent>
                    </Card>
                </TabsContent>

                {/* ── Activity Tab ────────────────────────── */}
                <TabsContent value="activity">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Activity className="h-5 w-5" /> Activity Timeline
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {id && <ActivityTimeline entityType="customer" entityId={id} limit={100} />}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {id && addContactOpen && <ContactFormDialog customerId={id} onClose={() => setAddContactOpen(false)} />}
            {id && editingContact && editingContact.legacyId && (
                <ContactFormDialog
                    customerId={id}
                    contact={{
                        id: editingContact.legacyId,
                        first_name: editingContact.first_name,
                        last_name: editingContact.last_name,
                        email: editingContact.email,
                        phone: editingContact.phone,
                        role_title: editingContact.role_title,
                        is_primary: editingContact.is_primary,
                    }}
                    onClose={() => setEditingContact(null)}
                />
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function InfoRow({ label, value, className }: { label: string; value: string; className?: string }) {
    return (
        <div className="flex justify-between">
            <span className="text-foreground/80">{label}</span>
            <span className={cn("font-medium text-right", className)}>{value}</span>
        </div>
    );
}

/**
 * Customer priority tier + monthly batch target admin. Tier drives the
 * tier-aware auto-scheduler ordering and the overflow lock (T1 at-risk locks
 * T3 scheduling). Office/Manager/Admin only; persists via the CRM PATCH which
 * accepts `priorityTier` / `monthlyBatchTargetOverride`.
 */
function PriorityTierPanel({
    customerId,
    tier,
    monthlyOverride,
}: {
    customerId: string;
    tier: string | null;
    monthlyOverride: number | null;
}) {
    const { isOffice } = useAuth();
    const qc = useQueryClient();
    const [tierValue, setTierValue] = useState(tier ?? "");
    const [overrideValue, setOverrideValue] = useState(monthlyOverride == null ? "" : String(monthlyOverride));

    useEffect(() => {
        setTierValue(tier ?? "");
        setOverrideValue(monthlyOverride == null ? "" : String(monthlyOverride));
    }, [tier, monthlyOverride]);

    const mutation = useMutation({
        mutationFn: () => {
            const body: { priorityTier?: string; monthlyBatchTargetOverride?: number | null } = {};
            if (tierValue) body.priorityTier = tierValue;
            const trimmed = overrideValue.trim();
            body.monthlyBatchTargetOverride = trimmed === "" ? null : Number(trimmed);
            return api.patch(`/api/customers/${customerId}`, body);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", customerId] });
            toast.success("Priority settings saved");
        },
        onError: () => toast.error("Failed to save priority settings"),
    });

    if (!isOffice) return null;

    const dirty =
        tierValue !== (tier ?? "") || overrideValue !== (monthlyOverride == null ? "" : String(monthlyOverride));

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                    <Star className="h-4 w-4" /> Priority &amp; Scheduling
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground/80">Priority tier</label>
                    <Select value={tierValue} onChange={(e) => setTierValue(e.target.value)}>
                        <option value="">— Unset (treated as T3) —</option>
                        <option value="T1">T1 — Priority batch</option>
                        <option value="T2">T2 — Standard</option>
                        <option value="T3">T3 — Overflow</option>
                    </Select>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground/80">Monthly batch target override</label>
                    <Input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        placeholder="Auto (derived from due assets)"
                        value={overrideValue}
                        onChange={(e) => setOverrideValue(e.target.value)}
                    />
                    <p className="text-[11px] text-foreground/60">
                        Leave blank to auto-derive the T1 burn target from compliance-due assets.
                    </p>
                </div>
                <Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending || !dirty}>
                    {mutation.isPending ? "Saving…" : "Save"}
                </Button>
            </CardContent>
        </Card>
    );
}

/**
 * #887 — "This customer requires a WO number." Office-only toggle writing
 * customers.requires_customer_wo; with it on, jobs for this account can't be
 * completed until a WO reference (WO # or PO #) is on the job.
 */
/**
 * #905 — "Payment reminders" mode for this account. Automatic = the machine
 * may email them when reminders are turned on org-wide; hand-managed = a
 * person owns this account and it is never auto-emailed.
 */
function CollectionsModeRow({ customerId, value }: { customerId: string; value: "auto" | "hand_managed" }) {
    const { isOffice } = useAuth();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationFn: (next: "auto" | "hand_managed") =>
            api.patch(`/api/customers/${customerId}`, { collectionsMode: next }),
        onSuccess: (_data, next) => {
            qc.invalidateQueries({ queryKey: ["customer", customerId] });
            toast.success(
                next === "hand_managed"
                    ? "This account is hand-managed — automatic reminders will never email them."
                    : "This account can receive automatic payment reminders when they're turned on.",
            );
        },
        onError: () => toast.error("Failed to update the reminders setting"),
    });
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-foreground/70">Payment reminders</span>
            {isOffice ? (
                <select
                    className="rounded-md border bg-background px-2 py-1 text-xs"
                    value={value}
                    disabled={mutation.isPending}
                    onChange={(e) => mutation.mutate(e.target.value === "hand_managed" ? "hand_managed" : "auto")}
                    data-testid="collections-mode-select"
                >
                    <option value="auto">Automatic</option>
                    <option value="hand_managed">We handle this account by hand</option>
                </select>
            ) : (
                <span className="text-xs">{value === "hand_managed" ? "Handled by hand" : "Automatic"}</span>
            )}
        </div>
    );
}

function RequiresWoToggleRow({ customerId, value }: { customerId: string; value: boolean }) {
    const { isOffice } = useAuth();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationFn: (next: boolean) => api.patch(`/api/customers/${customerId}`, { requiresCustomerWo: next }),
        onSuccess: (_data, next) => {
            qc.invalidateQueries({ queryKey: ["customer", customerId] });
            toast.success(
                next
                    ? "Jobs for this customer now need a WO number before close-out."
                    : "WO number no longer required for this customer's jobs.",
            );
        },
        onError: () => toast.error("Failed to update the WO requirement"),
    });
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-foreground/70">Requires WO #</span>
            {isOffice ? (
                <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium">
                    <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={value}
                        disabled={mutation.isPending}
                        onChange={(e) => mutation.mutate(e.target.checked)}
                        data-testid="requires-wo-toggle"
                    />
                    {value ? "Required at close-out" : "Not required"}
                </label>
            ) : (
                <span className="text-xs">{value ? "Required at close-out" : "Not required"}</span>
            )}
        </div>
    );
}

function DetailSkeleton() {
    return (
        <div className="space-y-6">
            <div>
                <Skeleton className="h-4 w-32 mb-3" />
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-48 mt-2" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="rounded-xl border bg-card p-6 space-y-3">
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-8 w-1/3" />
                    </div>
                ))}
            </div>
            <Skeleton className="h-10 w-full max-w-2xl" />
            <div className="grid gap-4 lg:grid-cols-3">
                <Skeleton className="h-64 rounded-xl" />
                <Skeleton className="h-64 rounded-xl lg:col-span-2" />
            </div>
        </div>
    );
}

function AiCustomerInsight({ customerId }: { customerId: string }) {
    const [insight, setInsight] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: () => api.get<{ insight: string }>(`/api/ai/customers/${customerId}/insight`),
        onSuccess: (data) => setInsight(data.insight),
    });

    return (
        <Card className="lg:col-span-3">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" /> AI Customer Insight
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                    {mutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                        <Sparkles className="h-3.5 w-3.5 mr-1" />
                    )}
                    {insight ? "Refresh" : "Generate Insight"}
                </Button>
            </CardHeader>
            <CardContent>
                {insight ? (
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/20 p-3">
                        <p className="text-sm">{insight}</p>
                    </div>
                ) : (
                    <p className="text-sm text-foreground/80">
                        Click "Generate Insight" for an AI-powered analysis of this customer's health, payment behavior,
                        and service recommendations.
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

// ────────────────────────────────────────────────────────────
// New Job button — pre-selects the customer (and only site if exactly one)
// ────────────────────────────────────────────────────────────

function NewJobButton({ customerId }: { customerId: string }) {
    const { data } = useQuery({
        queryKey: ["customer", customerId, "sites", "new-job"],
        queryFn: () => api.get<{ sites: { id: string }[] }>(`/api/customers/${customerId}/sites`),
        enabled: !!customerId,
    });
    const sites = data?.sites ?? [];
    const params = new URLSearchParams({ customerId });
    if (sites.length === 1) params.set("siteId", sites[0].id);
    return (
        <Link
            to={`/jobs/new?${params.toString()}`}
            className={cn(buttonVariants(), "bg-primary text-primary-foreground hover:bg-primary/90")}
        >
            <Plus className="h-4 w-4" /> New job
        </Link>
    );
}

// ────────────────────────────────────────────────────────────
// Locations tab — lists every service address attached to this
// customer with its jurisdiction + reporting-platform account
// number, and lets the office add or edit one. The account number
// lives on the site (not on the job) so every future visit reuses it.
// ────────────────────────────────────────────────────────────

interface SiteRow {
    id: string;
    customer_id: string;
    name: string | null;
    address_line: string | null;
    address_line2: string | null;
    city: string | null;
    region: string | null;
    postal_code: string | null;
    latitude: number | string | null;
    longitude: number | string | null;
    is_active: boolean;
    water_provider_id: string | null;
    water_provider_name?: string | null;
    water_provider_submission_platform?: string | null;
    water_provider_portal_url?: string | null;
    /** Canonical portal account (`sites.water_account_number`). */
    water_account_number?: string | null;
    /** Legacy ST location code — never shown as the portal account. */
    account_number: string | null;
    phone?: string | null;
    default_contact_id?: string | null;
    asset_count?: number;
    /**
     * The site work rollup (#985). Present on the Locations tab's list; the
     * cells render "we don't know" rather than good news when it is absent.
     */
    rollup?: SiteRollup | null;
}

interface WorkOrderTemplateRow {
    id: string;
    name: string;
    form_code: string;
    status: string;
    pdf_page_count: number;
    updated_at: string;
    field_count: number;
    mapped_field_count: number;
}

interface WorkOrderIntakeRow {
    id: string;
    status: string;
    created_at: string;
    file_name: string;
}

/**
 * Customer "Work Orders" tab. The maintenance group's work-order PDFs are
 * scanned in here and mapped directly on the customer — separate from the
 * jurisdiction form mapper. Scan reuses the existing /work-order-intake
 * pipeline (defaulting the customer), and "Map" turns a scanned PDF into a
 * work-order template (reusing a prior map when the layout is recognized),
 * then opens the shared visual editor.
 */
function WorkOrdersTabPanel({ customerId }: { customerId: string }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [settingsFor, setSettingsFor] = useState<string | null>(null);

    const templatesQ = useQuery({
        queryKey: ["customer", customerId, "work-order-templates"],
        queryFn: () =>
            api.get<{ templates: WorkOrderTemplateRow[] }>(`/api/customers/${customerId}/work-order-templates`),
        enabled: !!customerId,
    });

    // "Make default for…" (2026-10-29): a default form per kind of job. A
    // work order that matched no form fills on the default for its job type;
    // with none, the job waits with "Needs a return form".
    const defaultsQ = useQuery({
        queryKey: ["customer", customerId, "work-order-defaults"],
        queryFn: () => api.get<{ groups: DefaultGroup[] }>(`/api/customers/${customerId}/work-order-defaults`),
        enabled: !!customerId,
    });
    const defaultGroups = defaultsQ.data?.groups ?? [];
    const [pickingFor, setPickingFor] = useState<string | null>(null);

    const intakeQ = useQuery({
        queryKey: ["customer", customerId, "work-order-intake"],
        queryFn: () =>
            api.get<{ items: WorkOrderIntakeRow[] }>(`/api/work-order-intake/items?customerId=${customerId}&limit=100`),
        enabled: !!customerId,
    });

    const uploadM = useMutation({
        mutationFn: async (file: File) => {
            const fd = new FormData();
            fd.append("file", file);
            let itemId: string;
            try {
                const up = await api.postForm<{ itemId: string }>("/api/work-order-intake/upload", fd);
                itemId = up.itemId;
            } catch (e) {
                // Re-uploading the same PDF returns 409 with the existing item.
                if (e instanceof Error && "status" in e && (e as { status?: number }).status === 409) {
                    const body = (e as { body?: { itemId?: string } }).body;
                    if (body?.itemId) itemId = body.itemId;
                    else throw e;
                } else {
                    throw e;
                }
            }
            // Tag the scanned work order to this customer so it shows here.
            await api.patch(`/api/work-order-intake/items/${itemId}`, { matchedCustomerId: customerId });
            return { itemId };
        },
        onSuccess: () => {
            toast.success("Work order scanned in");
            qc.invalidateQueries({ queryKey: ["customer", customerId, "work-order-intake"] });
        },
        onError: () => toast.error("Could not scan the work order"),
    });

    const mapM = useMutation({
        mutationFn: (intakeItemId: string) =>
            api.post<{ templateId: string; reused: boolean; intakeItemId?: string }>(
                `/api/customers/${customerId}/work-order-templates/from-intake`,
                { intakeItemId },
            ),
        onSuccess: (data, intakeItemId) => {
            toast.success(
                data.reused ? "Recognized this layout — opening review" : "Template created — opening review",
            );
            qc.invalidateQueries({ queryKey: ["customer", customerId, "work-order-templates"] });
            // The full-screen setup page is the setup surface — confirming
            // requirements there generates the field map, config, and activates.
            navigate(`/jobs/work-orders/setup/${intakeItemId}`);
        },
        onError: () => toast.error("Could not build a template from this work order"),
    });

    const templates = templatesQ.data?.templates ?? [];
    const intakeItems = intakeQ.data?.items ?? [];

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <FileStack className="h-4 w-4" /> Work Order Forms
                    </CardTitle>
                    <div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadM.mutate(f);
                                e.target.value = "";
                            }}
                        />
                        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploadM.isPending}>
                            {uploadM.isPending ? (
                                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4 mr-1.5" />
                            )}
                            Scan / upload work order
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-foreground/80 mb-3">
                        Scan a work order in, then map it once — the next time this maintenance group sends the same
                        form, it&apos;s recognized automatically and reuses your map.
                    </p>
                    {templatesQ.isLoading ? (
                        <Skeleton className="h-20 w-full" />
                    ) : templates.length === 0 ? (
                        <p className="text-sm text-foreground/80">
                            No mapped work-order forms yet. Scan one above to get started.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {defaultsQ.isSuccess && <WoDefaultsStrip groups={defaultGroups} />}
                            {templates.map((t) => (
                                <div
                                    key={t.id}
                                    className={cn(
                                        "rounded-lg border",
                                        defaultGroups.some((g) => g.templateId === t.id) &&
                                            "border-l-4 border-l-primary",
                                    )}
                                >
                                    <div className="flex items-center justify-between p-3">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">{t.name}</p>
                                            <p className="text-xs text-foreground/70">
                                                {t.mapped_field_count}/{t.field_count} fields mapped ·{" "}
                                                {t.pdf_page_count} page{t.pdf_page_count === 1 ? "" : "s"} ·{" "}
                                                <span className="capitalize">{t.status}</span>
                                            </p>
                                            <DefaultForTags groups={defaultGroups} templateId={t.id} />
                                            {t.status !== "active" && (
                                                <p className="mt-1 text-xs italic text-foreground/60">
                                                    Lock this form first to make it a default.
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {t.status === "active" && defaultsQ.isSuccess && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className={cn(
                                                        "border-primary text-primary",
                                                        pickingFor === t.id && "bg-primary/10",
                                                    )}
                                                    title="Pick the kinds of job that fill on this form when their work order matched no form."
                                                    onClick={() => setPickingFor((cur) => (cur === t.id ? null : t.id))}
                                                >
                                                    Make default for…
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setSettingsFor((cur) => (cur === t.id ? null : t.id))}
                                            >
                                                <Settings className="h-3.5 w-3.5 mr-1.5" />
                                                {settingsFor === t.id ? "Hide settings" : "Settings"}
                                            </Button>
                                            {/* WO rework slice 6: the 4-step pins flow is the
                                                front door; the full mapper is "Advanced". */}
                                            <Link
                                                to={`/admin/form-templates/${t.id}/new`}
                                                className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                                            >
                                                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Check the pins
                                            </Link>
                                            <Link
                                                to={`/admin/form-templates/${t.id}`}
                                                className="text-xs text-foreground/70 underline"
                                            >
                                                Advanced
                                            </Link>
                                        </div>
                                    </div>
                                    {pickingFor === t.id && (
                                        <div className="px-3 pb-3">
                                            <MakeDefaultForPicker
                                                customerId={customerId}
                                                templateId={t.id}
                                                groups={defaultGroups}
                                                onDone={() => setPickingFor(null)}
                                            />
                                        </div>
                                    )}
                                    {settingsFor === t.id && (
                                        <div className="border-t p-3">
                                            <WorkOrderSettingsPanel customerId={customerId} templateId={t.id} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Scanned work orders</CardTitle>
                </CardHeader>
                <CardContent>
                    {intakeQ.isLoading ? (
                        <Skeleton className="h-16 w-full" />
                    ) : intakeItems.length === 0 ? (
                        <p className="text-sm text-foreground/80">Nothing scanned yet for this customer.</p>
                    ) : (
                        <div className="space-y-2">
                            {intakeItems.map((it) => (
                                <div key={it.id} className="flex items-center justify-between rounded-lg border p-3">
                                    <div className="min-w-0">
                                        <WoPdfHoverPreview itemId={it.id}>
                                            <p className="text-sm font-medium truncate">{it.file_name}</p>
                                        </WoPdfHoverPreview>
                                        <p className="text-xs text-foreground/70">
                                            {new Date(it.created_at).toLocaleString()} ·{" "}
                                            <Badge variant="secondary">{it.status}</Badge>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => navigate(`/jobs/work-orders/setup/${it.id}`)}
                                        >
                                            <FileText className="h-3.5 w-3.5 mr-1.5" />
                                            Review extraction
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => mapM.mutate(it.id)}
                                            disabled={mapM.isPending}
                                        >
                                            {mapM.isPending ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            ) : (
                                                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                                            )}
                                            Map this work order
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

interface CompletionFieldDef {
    key: string;
    label: string;
    type: "checkbox" | "text" | "select";
    options?: string[];
}

interface WorkOrderConfig {
    id: string;
    form_template_id: string;
    customer_id: string;
    default_job_type_id: string | null;
    ivr_phone: string | null;
    ivr_pin_field: string | null;
    delivery_email: string | null;
    auto_deliver: boolean;
    requires_confirmation_number: boolean;
    completion_fields: CompletionFieldDef[];
    office_notes: string | null;
}

interface JobTypeOption {
    id: string;
    name: string;
    code: string;
}

/**
 * Per-template "Work Order Settings". Configures the customer-specific
 * behavior that drives auto job creation (default job type), the field
 * briefing (IVR number/pin), completion form (extra fields,
 * confirmation-number requirement), and delivery (email + auto-send).
 * Maps 1:1 to the work_order_template_config row.
 */
function WorkOrderSettingsPanel({ customerId, templateId }: { customerId: string; templateId: string }) {
    const qc = useQueryClient();

    const configQ = useQuery({
        queryKey: ["customer", customerId, "wo-template-config", templateId],
        queryFn: () =>
            api.get<{ config: WorkOrderConfig | null }>(
                `/api/customers/${customerId}/work-order-templates/${templateId}/config`,
            ),
    });

    const jobTypesQ = useQuery({
        queryKey: ["job-types", "active"],
        queryFn: () => api.get<{ jobTypes: JobTypeOption[] }>(`/api/job-types`),
        staleTime: 5 * 60_000,
    });

    const [defaultJobTypeId, setDefaultJobTypeId] = useState("");
    const [ivrPhone, setIvrPhone] = useState("");
    const [ivrPinField, setIvrPinField] = useState("");
    const [deliveryEmail, setDeliveryEmail] = useState("");
    const [autoDeliver, setAutoDeliver] = useState(false);
    const [requiresConfirmation, setRequiresConfirmation] = useState(false);
    const [officeNotes, setOfficeNotes] = useState("");
    const [completionFields, setCompletionFields] = useState<CompletionFieldDef[]>([]);

    useEffect(() => {
        const c = configQ.data?.config;
        if (!c) return;
        setDefaultJobTypeId(c.default_job_type_id ?? "");
        setIvrPhone(c.ivr_phone ?? "");
        setIvrPinField(c.ivr_pin_field ?? "");
        setDeliveryEmail(c.delivery_email ?? "");
        setAutoDeliver(!!c.auto_deliver);
        setRequiresConfirmation(!!c.requires_confirmation_number);
        setOfficeNotes(c.office_notes ?? "");
        setCompletionFields(Array.isArray(c.completion_fields) ? c.completion_fields : []);
    }, [configQ.data]);

    const saveM = useMutation({
        mutationFn: () =>
            api.put(`/api/customers/${customerId}/work-order-templates/${templateId}/config`, {
                defaultJobTypeId: defaultJobTypeId || null,
                ivrPhone: ivrPhone.trim() || null,
                ivrPinField: ivrPinField.trim() || null,
                deliveryEmail: deliveryEmail.trim() || null,
                autoDeliver,
                requiresConfirmationNumber: requiresConfirmation,
                completionFields: completionFields
                    .filter((f) => f.key.trim() && f.label.trim())
                    .map((f) => ({
                        key: f.key.trim(),
                        label: f.label.trim(),
                        type: f.type,
                        ...(f.type === "select"
                            ? { options: (f.options ?? []).map((o) => o.trim()).filter(Boolean) }
                            : {}),
                    })),
                officeNotes: officeNotes.trim() || null,
            }),
        onSuccess: () => {
            toast.success("Work order settings saved");
            qc.invalidateQueries({ queryKey: ["customer", customerId, "wo-template-config", templateId] });
        },
        onError: () => toast.error("Could not save settings"),
    });

    const updateField = (idx: number, patch: Partial<CompletionFieldDef>) =>
        setCompletionFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

    if (configQ.isLoading) return <Skeleton className="h-40 w-full" />;

    const jobTypes = jobTypesQ.data?.jobTypes ?? [];

    return (
        <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground/80">Default job type</label>
                    <Select value={defaultJobTypeId} onChange={(e) => setDefaultJobTypeId(e.target.value)}>
                        <option value="">— Office picks on review —</option>
                        {jobTypes.map((jt) => (
                            <option key={jt.id} value={jt.id}>
                                {jt.name}
                            </option>
                        ))}
                    </Select>
                    <p className="text-[11px] text-foreground/60">
                        Auto-created jobs use this type. Leave blank to require office review.
                    </p>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground/80">IVR phone (tap-to-call)</label>
                    <Input
                        value={ivrPhone}
                        onChange={(e) => setIvrPhone(e.target.value)}
                        placeholder="e.g. 844-347-3487"
                    />
                    <p className="text-[11px] text-foreground/60">
                        Customer-wide IVR number. Leave blank if the number is per-location on the work order.
                    </p>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground/80">IVR pin field key</label>
                    <Input
                        value={ivrPinField}
                        onChange={(e) => setIvrPinField(e.target.value)}
                        placeholder="e.g. ivr_pin"
                    />
                    <p className="text-[11px] text-foreground/60">
                        Extracted-value key holding the location-specific IVR pin, if any.
                    </p>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground/80">Delivery email</label>
                    <Input
                        type="email"
                        value={deliveryEmail}
                        onChange={(e) => setDeliveryEmail(e.target.value)}
                        placeholder="e.g. documents@lifesafetysecurity.com"
                    />
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={autoDeliver} onCheckedChange={(v) => setAutoDeliver(v === true)} />
                    Auto-send completed work order to the delivery email
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={requiresConfirmation}
                        onCheckedChange={(v) => setRequiresConfirmation(v === true)}
                    />
                    IVR returns a confirmation number the tech should record
                </label>
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-foreground/80">
                        Completion form fields (beyond the universal set)
                    </label>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            setCompletionFields((prev) => [...prev, { key: "", label: "", type: "checkbox" }])
                        }
                    >
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Add field
                    </Button>
                </div>
                {completionFields.length === 0 ? (
                    <p className="text-[11px] text-foreground/60">
                        No customer-specific completion fields. The universal fields always show.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {completionFields.map((f, idx) => (
                            <div key={idx} className="grid gap-2 sm:grid-cols-12 items-start rounded-md border p-2">
                                <Input
                                    className="sm:col-span-3"
                                    value={f.key}
                                    onChange={(e) => updateField(idx, { key: e.target.value })}
                                    placeholder="key"
                                />
                                <Input
                                    className="sm:col-span-4"
                                    value={f.label}
                                    onChange={(e) => updateField(idx, { label: e.target.value })}
                                    placeholder="Label shown to tech"
                                />
                                <Select
                                    className="sm:col-span-2"
                                    value={f.type}
                                    onChange={(e) =>
                                        updateField(idx, {
                                            type: e.target.value as CompletionFieldDef["type"],
                                        })
                                    }
                                >
                                    <option value="checkbox">checkbox</option>
                                    <option value="text">text</option>
                                    <option value="select">select</option>
                                </Select>
                                <Input
                                    className="sm:col-span-2"
                                    value={(f.options ?? []).join(", ")}
                                    onChange={(e) =>
                                        updateField(idx, {
                                            options: e.target.value.split(",").map((o) => o.trim()),
                                        })
                                    }
                                    placeholder={f.type === "select" ? "opt1, opt2" : "—"}
                                    disabled={f.type !== "select"}
                                />
                                <div className="sm:col-span-1 flex justify-end">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => setCompletionFields((prev) => prev.filter((_, i) => i !== idx))}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground/80">Office-only notes</label>
                <Textarea
                    value={officeNotes}
                    onChange={(e) => setOfficeNotes(e.target.value)}
                    placeholder="Invoicing requirements, payment terms, billing contact (never shown to the tech)"
                />
            </div>

            <div className="flex justify-end">
                <Button onClick={() => saveM.mutate()} disabled={saveM.isPending}>
                    {saveM.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                        <Save className="h-4 w-4 mr-1.5" />
                    )}
                    Save settings
                </Button>
            </div>
        </div>
    );
}

/**
 * The Locations tab — the book of business (#992).
 *
 * On a 1,088-location national account the location IS the unit of work, so
 * this stopped being a list of addresses. Named buckets across the top, one
 * row per location carrying its compliance state, devices, last visit, open
 * work, money and filing readiness, all sorted and filtered SERVER-side
 * against the whole account rather than the visible page.
 *
 * The view lives in the URL, so "show me every Cintas location we can't file
 * for" is a link somebody can send.
 */
function LocationsTabPanel({ customerId }: { customerId: string }) {
    const { can } = useAuth();
    const canPriceCheck = can(PERMISSIONS.pricing.view);
    const [addOpen, setAddOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const bucket = searchParams.get("bucket") ?? "all";
    // A sort in the URL (a shared link, the back button) wins. Without one,
    // the tab opens in the order this person last picked, on any customer.
    const savedSort = usePersistedSort({
        tableId: "customer.locations",
        defaultKey: "location",
        validKeys: LOCATION_SORT_KEYS,
    });
    const urlSortValid = (LOCATION_SORT_KEYS as ReadonlyArray<string>).includes(searchParams.get("sort") ?? "");
    const sort: LocationSortKey = urlSortValid
        ? (searchParams.get("sort") as LocationSortKey)
        : ((savedSort.sortKey as LocationSortKey | null) ?? "location");
    const dir: "asc" | "desc" =
        searchParams.get("dir") === "asc"
            ? "asc"
            : searchParams.get("dir") === "desc"
              ? "desc"
              : !urlSortValid
                ? savedSort.sortDir
                : sort === "location"
                  ? "asc"
                  : "desc";
    const city = searchParams.get("city") ?? "";
    const jurisdiction = searchParams.get("jurisdiction") ?? "";
    const urlQ = searchParams.get("q") ?? "";

    const [search, setSearch] = useState(urlQ);
    const debouncedSearch = useDebouncedValue(search, 250);

    // One writer for the whole view so a bucket click can't strip the search
    // and a sort can't strip the bucket. Paging resets whenever the view
    // changes — page 3 of a different question is nobody's intent.
    const updateView = useCallback(
        (patch: Record<string, string | null>) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    for (const [k, v] of Object.entries(patch)) {
                        if (v === null || v === "") next.delete(k);
                        else next.set(k, v);
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    // Keep the URL in step with the debounced box, not every keystroke.
    useEffect(() => {
        if (debouncedSearch !== urlQ) updateView({ q: debouncedSearch.trim() || null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const listParams = useMemo(() => {
        const p = new URLSearchParams();
        if (debouncedSearch.trim()) p.set("q", debouncedSearch.trim());
        if (bucket !== "all") p.set("bucket", bucket);
        if (city) p.set("city", city);
        if (jurisdiction) p.set("jurisdiction", jurisdiction);
        p.set("sort", sort);
        p.set("dir", dir);
        return p;
    }, [debouncedSearch, bucket, city, jurisdiction, sort, dir]);

    const SITES_PAGE = 50;
    const sitesQ = useInfiniteQuery({
        queryKey: ["customer", customerId, "sites", "tab", listParams.toString()],
        queryFn: ({ pageParam = 0 }) => {
            const params = new URLSearchParams(listParams);
            params.set("limit", String(SITES_PAGE));
            params.set("offset", String(pageParam));
            return api.get<{ sites: SiteRow[]; total: number; matchNote: string | null }>(
                `/api/customers/${customerId}/sites?${params.toString()}`,
            );
        },
        initialPageParam: 0,
        getNextPageParam: (_lastPage, allPages) => {
            const loaded = allPages.reduce((n, p) => n + p.sites.length, 0);
            return loaded < (allPages[0]?.total ?? 0) ? loaded : undefined;
        },
        // Wait for the saved sort so the list doesn't load twice.
        enabled: !!customerId && (urlSortValid || savedSort.loaded),
    });

    // The strip's counts follow the same search and filters as the list, so
    // narrowing the search narrows the numbers with it.
    const bucketParams = useMemo(() => {
        const p = new URLSearchParams();
        if (debouncedSearch.trim()) p.set("q", debouncedSearch.trim());
        if (city) p.set("city", city);
        if (jurisdiction) p.set("jurisdiction", jurisdiction);
        return p;
    }, [debouncedSearch, city, jurisdiction]);

    const bucketsQ = useQuery({
        queryKey: ["customer", customerId, "site-buckets", bucketParams.toString()],
        queryFn: () =>
            api.get<{
                buckets: SiteBucketChip[];
                cities: string[];
                jurisdictions: Array<{ id: string; name: string }>;
                jurisdictionNoneCount: number;
            }>(`/api/customers/${customerId}/site-buckets?${bucketParams.toString()}`),
        enabled: !!customerId,
    });

    const providersQ = useWaterProviders();

    const sites = sitesQ.data?.pages.flatMap((p) => p.sites) ?? [];

    // #1039: selection lives here, not in the table, so clearing the bucket
    // filter clears it too — a selection that outlives the filter it was made
    // under is how you act on rows you can no longer see.
    const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
    const toggleSite = (id: string) =>
        setSelectedSites((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const toggleAllShown = (ids: string[]) =>
        setSelectedSites((prev) => (ids.every((id) => prev.has(id)) ? new Set() : new Set(ids)));

    // Clear the selection whenever the filter moves. A selection made under
    // "Overdue" and acted on after switching to "All" would create jobs at
    // locations the operator can no longer see, which is the whole nightmare
    // this feature has to avoid.
    const selectionScopeKey = `${bucket}|${searchParams.get("q") ?? ""}|${searchParams.get("city") ?? ""}|${
        searchParams.get("jurisdiction") ?? ""
    }`;
    const lastScopeRef = useRef(selectionScopeKey);
    useEffect(() => {
        if (lastScopeRef.current !== selectionScopeKey) {
            lastScopeRef.current = selectionScopeKey;
            setSelectedSites(new Set());
        }
    }, [selectionScopeKey]);
    const sitesTotal = sitesQ.data?.pages[0]?.total ?? sites.length;
    const matchNote = sitesQ.data?.pages[0]?.matchNote ?? null;
    const editingSite = editingId ? (sites.find((s) => s.id === editingId) ?? null) : null;
    const allCount = bucketsQ.data?.buckets.find((b) => b.id === "all")?.count ?? null;

    const isFiltered = Boolean(debouncedSearch.trim() || city || jurisdiction || bucket !== "all");
    // Two different sentences, because they are two different situations. A
    // search that matches nothing on a 1,088-address account must never read
    // "No service addresses yet."
    const emptyMessage = isFiltered
        ? "No locations match this view. Clear the search or pick another bucket."
        : "No service addresses yet. Add the first one to start scheduling jobs here.";

    const activeBucketLabel = bucketsQ.data?.buckets.find((b) => b.id === bucket)?.label ?? null;

    return (
        <>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <MapPin className="h-4 w-4" /> Locations ({allCount ?? sitesTotal})
                    </CardTitle>
                    <Button size="sm" onClick={() => setAddOpen(true)}>
                        <Plus className="mr-1.5 h-4 w-4" /> Add location
                    </Button>
                </CardHeader>
                <CardContent>
                    <BucketStrip
                        buckets={bucketsQ.data?.buckets ?? []}
                        active={bucket}
                        loading={bucketsQ.isLoading}
                        onSelect={(id) => updateView({ bucket: id === "all" ? null : id })}
                    />
                    <TabToolbar
                        searchValue={search}
                        onSearchChange={setSearch}
                        searchPlaceholder="Search by name, address, city, or account # — e.g. “Petsmart Hurst”"
                        rightSlot={
                            <div className="flex gap-2">
                                <Select
                                    value={city}
                                    onChange={(e) => updateView({ city: e.target.value || null })}
                                    className="w-full sm:w-40"
                                    aria-label="Filter by city"
                                >
                                    <option value="">All cities</option>
                                    {(bucketsQ.data?.cities ?? []).map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                                </Select>
                                <Select
                                    value={jurisdiction}
                                    onChange={(e) => updateView({ jurisdiction: e.target.value || null })}
                                    className="w-full sm:w-48"
                                    aria-label="Filter by water provider"
                                >
                                    <option value="">All water providers</option>
                                    {(bucketsQ.data?.jurisdictionNoneCount ?? 0) > 0 && (
                                        <option value="none">
                                            No water provider set ({bucketsQ.data?.jurisdictionNoneCount})
                                        </option>
                                    )}
                                    {(bucketsQ.data?.jurisdictions ?? []).map((j) => (
                                        <option key={j.id} value={j.id}>
                                            {j.name}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        }
                    />
                    {matchNote && <div className="mb-2 text-xs text-foreground/70">{matchNote}</div>}
                    {isFiltered && (
                        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-foreground/80">
                            <span>
                                Showing {sitesTotal} of {allCount ?? sitesTotal}
                                {activeBucketLabel && bucket !== "all" ? ` · ${activeBucketLabel}` : ""}
                            </span>
                            <button
                                type="button"
                                className="underline hover:text-foreground"
                                onClick={() => {
                                    setSearch("");
                                    updateView({ bucket: null, city: null, jurisdiction: null, q: null });
                                }}
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                    <LocationsTable
                        sites={sites}
                        customerId={customerId}
                        sort={sort}
                        dir={dir}
                        onSort={(key) => {
                            // Second click on the same column flips it; a new
                            // column starts at its useful default (A–Z for the
                            // name, worst / biggest first for everything else).
                            const nextDir =
                                key === sort ? (dir === "asc" ? "desc" : "asc") : key === "location" ? "asc" : "desc";
                            savedSort.setSort({ key, dir: nextDir });
                            updateView({ sort: key, dir: nextDir });
                        }}
                        onEdit={setEditingId}
                        canPriceCheck={canPriceCheck}
                        onPriceCheck={(providerId, providerName) => openPriceCheck({ providerId, providerName })}
                        loading={sitesQ.isPending}
                        emptyMessage={emptyMessage}
                        selected={selectedSites}
                        onToggleSelect={toggleSite}
                        onToggleAll={toggleAllShown}
                    />
                    <BatchBar
                        selected={[...selectedSites]}
                        // Says what the selection COVERS, out loud. On a
                        // 1,088-site account "select all" that quietly means
                        // the 25 rows loaded — or all 1,088 — is the difference
                        // between a batch and an incident.
                        scopeLabel={selectAllScopeLabel(
                            sites.length,
                            activeBucketLabel ?? "this list",
                            sitesTotal ?? sites.length,
                        )}
                        onClear={() => setSelectedSites(new Set())}
                        onDone={() => {
                            void sitesQ.refetch();
                            void bucketsQ.refetch();
                        }}
                    />
                    {sitesQ.hasNextPage && (
                        <div className="mt-3 flex items-center justify-center">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={sitesQ.isFetchingNextPage}
                                onClick={() => sitesQ.fetchNextPage()}
                            >
                                {sitesQ.isFetchingNextPage
                                    ? "Loading…"
                                    : `Load more (${sites.length} of ${sitesTotal})`}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {addOpen && (
                <SiteFormDialog
                    customerId={customerId}
                    mode="create"
                    providers={providersQ.data?.providers ?? []}
                    onClose={() => setAddOpen(false)}
                />
            )}
            {editingSite && (
                <SiteFormDialog
                    customerId={customerId}
                    mode="edit"
                    providers={providersQ.data?.providers ?? []}
                    site={editingSite}
                    onClose={() => setEditingId(null)}
                />
            )}
        </>
    );
}

interface SiteFormDialogProps {
    customerId: string;
    mode: "create" | "edit";
    providers: WaterProviderOption[];
    site?: SiteRow;
    onClose: () => void;
}

function SiteFormDialog({ customerId, mode, providers, site, onClose }: SiteFormDialogProps) {
    const qc = useQueryClient();
    const [name, setName] = useState(site?.name ?? "");
    const [addressLine, setAddressLine] = useState(site?.address_line ?? "");
    const [addressLine2, setAddressLine2] = useState(site?.address_line2 ?? "");
    const [city, setCity] = useState(site?.city ?? "");
    const [region, setRegion] = useState(site?.region ?? "");
    const [postalCode, setPostalCode] = useState(site?.postal_code ?? "");

    // Water & Reporting is the shared editor's fieldset. In edit mode the
    // values seed from a FRESH by-id read (`["site-water", id]`), never the
    // cached locations-list row — the old dialog once showed a stale
    // placeholder while both site columns already held the real number.
    const freshSiteQ = useQuery({
        queryKey: ["site-water", site?.id],
        queryFn: () =>
            api.get<{
                site: {
                    water_provider_id: string | null;
                    water_provider_name?: string | null;
                    water_provider_display_name?: string | null;
                    water_account_number?: string | null;
                    account_number: string | null;
                    phone?: string | null;
                    default_contact_id?: string | null;
                };
            }>(`/api/sites/${site!.id}`),
        enabled: mode === "edit" && !!site?.id,
        staleTime: 0,
        refetchOnMount: "always",
    });
    const freshSite = freshSiteQ.data?.site ?? null;
    const [waterDraft, setWaterDraft] = useState<WaterReportingValue | null>(null);
    const water: WaterReportingValue = waterDraft ?? {
        waterProviderId: freshSite?.water_provider_id ?? site?.water_provider_id ?? "",
        // Canonical portal account only — never the legacy ST code.
        waterAccountNumber: freshSite?.water_account_number?.trim() ?? site?.water_account_number?.trim() ?? "",
        phone: freshSite?.phone ?? site?.phone ?? "",
        defaultContactId: freshSite?.default_contact_id ?? site?.default_contact_id ?? "",
    };

    // Contacts for the "Site contact" picker. These belong to the customer, so
    // the office can point a location at whoever is on-site (property manager,
    // reception) without re-typing them. Cached with the contacts tab query.
    const contactsQ = useQuery({
        queryKey: ["customer", customerId, "contacts"],
        queryFn: () => api.get<{ contacts: ContactRow[] }>(`/api/customers/${customerId}/contacts`),
    });
    const contacts = contactsQ.data?.contacts ?? [];
    // Pin state. We coerce numeric strings (Postgres returns NUMERIC as
    // strings via node-postgres) so the map gets actual numbers.
    const initialLat = site?.latitude == null ? null : Number(site.latitude);
    const initialLng = site?.longitude == null ? null : Number(site.longitude);
    const [latitude, setLatitude] = useState<number | null>(Number.isFinite(initialLat) ? initialLat : null);
    const [longitude, setLongitude] = useState<number | null>(Number.isFinite(initialLng) ? initialLng : null);
    // Track whether the office has manually moved the pin. After a drag,
    // typing into the address fields shouldn't quietly reposition them
    // back to the geocoder result — manual intent wins.
    const [pinManual, setPinManual] = useState<boolean>(Number.isFinite(initialLat) && Number.isFinite(initialLng));
    const [previewing, setPreviewing] = useState(false);
    // True when the last auto-geocode ran but couldn't resolve the address, so
    // the UI can tell the office "couldn't locate — drop a pin" instead of
    // silently leaving the map empty.
    const [geocodeMiss, setGeocodeMiss] = useState(false);

    // Debounced auto-geocode preview. Fires 600ms after the office stops
    // typing in the address fields (and never overrides a manually
    // dragged pin). Uses POST /api/sites/geocode-preview which is a
    // read-only wrapper around the same geocoder we use on save — so
    // the pin the office sees here is exactly the pin a save without
    // manual override would persist.
    useEffect(() => {
        if (pinManual) return;
        const line = addressLine.trim();
        const cityT = city.trim();
        const regionT = region.trim();
        const zipT = postalCode.trim();
        if (!line && !cityT && !regionT && !zipT) return;
        let cancelled = false;
        const handle = window.setTimeout(async () => {
            setPreviewing(true);
            try {
                const result = await api.post<{
                    coordinates: { latitude: number; longitude: number } | null;
                }>("/api/sites/geocode-preview", {
                    addressLine: line || null,
                    city: cityT || null,
                    region: regionT || null,
                    postalCode: zipT || null,
                });
                if (cancelled) return;
                if (result.coordinates) {
                    setLatitude(result.coordinates.latitude);
                    setLongitude(result.coordinates.longitude);
                    setGeocodeMiss(false);
                } else {
                    setGeocodeMiss(true);
                }
            } catch {
                // Best-effort preview; missing network or mock-mode null
                // are both fine — the office can still drop a manual pin.
            } finally {
                if (!cancelled) setPreviewing(false);
            }
        }, 600);
        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [addressLine, city, region, postalCode, pinManual]);

    const mutation = useMutation({
        mutationFn: () => {
            const body: Record<string, unknown> = {
                name: name.trim() || addressLine.trim() || "Location",
                addressLine: addressLine.trim() || null,
                addressLine2: addressLine2.trim() || null,
                city: city.trim() || null,
                region: region.trim() || null,
                postalCode: postalCode.trim() || null,
                // Canonical Water & Reporting write — waterAccountNumber
                // (sites.water_account_number). NEVER accountNumber; the
                // legacy ST code isn't editable from the product.
                ...buildWaterReportingPatch(water),
            };
            // Only send coords when we have them; that way the server's
            // PATCH semantics stay clean ("caller didn't touch coords"
            // means re-geocode if address changed).
            if (latitude != null && longitude != null) {
                body.latitude = latitude;
                body.longitude = longitude;
            }
            if (mode === "create") {
                return api.post<{ site: SiteRow }>(`/api/customers/${customerId}/sites`, body);
            }
            return api.patch<{ site: SiteRow }>(`/api/sites/${site!.id}`, body);
        },
        onSuccess: (result: { site?: SiteRow }) => {
            toast.success(mode === "create" ? "Location added" : "Location updated");
            qc.invalidateQueries({ queryKey: ["customer", customerId] });
            qc.invalidateQueries({ queryKey: ["customer", customerId, "sites", "new-job"] });
            // Shared Water & Reporting invalidation: the site page card, any
            // open job's reporting card, the pre-job gate, and the filing
            // queue all reflect this save without a reload.
            const savedSiteId = site?.id ?? result.site?.id;
            if (savedSiteId) {
                invalidateWaterReporting(qc, { siteId: savedSiteId, customerId });
            } else {
                qc.invalidateQueries({ queryKey: ["customer", customerId, "sites", "tab"] });
            }
            onClose();
        },
        onError: (e: unknown) => {
            const msg = e instanceof Error ? e.message : "Failed to save location";
            toast.error(msg);
        },
    });

    const canSave = (addressLine.trim().length > 0 || (mode === "edit" && site)) && !mutation.isPending;

    return (
        <Dialog
            open={true}
            onClose={onClose}
            title={mode === "create" ? "Add location" : "Edit location"}
            className="max-w-xl"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (canSave) mutation.mutate();
                }}
                className="space-y-3"
            >
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2 space-y-1">
                        <label className="text-sm font-medium">Nickname</label>
                        <Input
                            placeholder="Main building, North warehouse, etc."
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <p className="text-xs text-foreground/80">Optional — defaults to the street address.</p>
                    </div>
                    <div className="sm:col-span-2 space-y-1">
                        <label className="text-sm font-medium">Street address</label>
                        <Input
                            required={mode === "create"}
                            placeholder="123 Main St"
                            value={addressLine}
                            onChange={(e) => setAddressLine(e.target.value)}
                        />
                    </div>
                    <div className="sm:col-span-2 space-y-1">
                        <label className="text-sm font-medium">Apt / suite (optional)</label>
                        <Input
                            placeholder="Suite 200"
                            value={addressLine2}
                            onChange={(e) => setAddressLine2(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">City</label>
                        <Input value={city} onChange={(e) => setCity(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                            <label className="text-sm font-medium">State</label>
                            <Input value={region} onChange={(e) => setRegion(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-medium">ZIP</label>
                            <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                        </div>
                    </div>
                    <div className="sm:col-span-2 space-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">Map location</label>
                            <div className="flex items-center gap-2 text-xs text-foreground/80">
                                {previewing && (
                                    <span className="flex items-center gap-1">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Looking up address…
                                    </span>
                                )}
                                {latitude != null && longitude != null && (
                                    <button
                                        type="button"
                                        className="text-primary hover:underline"
                                        onClick={() => {
                                            setLatitude(null);
                                            setLongitude(null);
                                            setPinManual(false);
                                        }}
                                    >
                                        Clear pin
                                    </button>
                                )}
                            </div>
                        </div>
                        {/* Explicit geocode confirmation, like a good form filler:
                            confirmed pin (green) vs. couldn't-locate prompt. */}
                        {!previewing && latitude != null && longitude != null && (
                            <div className="flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-800 dark:border-emerald-600/40 dark:bg-emerald-900/20 dark:text-emerald-200">
                                <MapPin className="h-3.5 w-3.5" />
                                {pinManual ? "Pin placed manually" : "Address found — pin placed"}
                            </div>
                        )}
                        {!previewing && geocodeMiss && latitude == null && longitude == null && (
                            <div className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800 dark:border-amber-600/40 dark:bg-amber-900/20 dark:text-amber-200">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Couldn't locate this address — tap the map to drop a pin.
                            </div>
                        )}
                        <LocationPickerMap
                            centerLat={latitude}
                            centerLng={longitude}
                            pinLat={latitude}
                            pinLng={longitude}
                            onPinChange={(lat, lng) => {
                                setLatitude(lat);
                                setLongitude(lng);
                                setPinManual(true);
                                setGeocodeMiss(false);
                            }}
                        />
                        <p className="text-xs text-foreground/80">
                            {pinManual
                                ? "Pin set manually — typing in the address fields won't move it."
                                : "Address looks right? Drag the pin to correct it. The dispatch route map plots stops by these coordinates."}
                        </p>
                    </div>
                    {/* ONE editor, every surface: the same Water & Reporting
                        fieldset the site page and job page mount. Reads/writes
                        the canonical water_account_number. */}
                    <div className="sm:col-span-2">
                        <WaterReportingFields
                            value={water}
                            onChange={setWaterDraft}
                            providers={providers}
                            contacts={contacts}
                            legacyAccountNumber={freshSite?.account_number ?? site?.account_number}
                            savedProviderName={
                                freshSite?.water_provider_display_name ||
                                freshSite?.water_provider_name ||
                                site?.water_provider_name
                            }
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={!canSave}>
                        {mutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                            <Save className="h-4 w-4 mr-1" />
                        )}
                        {mode === "create" ? "Add location" : "Save changes"}
                    </Button>
                </div>
            </form>
        </Dialog>
    );
}

// ────────────────────────────────────────────────────────────
// Intake handoff banner — shown when a customer was just created
// from the New Customer Intake form. Surfaces the most likely next
// steps (start a job, queue first test, send a quote) so the office
// doesn't have to hunt for them.
// ────────────────────────────────────────────────────────────

interface IntakeHandoffBannerProps {
    customerId: string;
    customerName: string;
    siteId: string;
    intakeNotes: string;
    similar: Array<{ id: string; company_name: string; match_reason: string }>;
    onDismiss: () => void;
}

/**
 * #1030 — the "Do Not Service" banner. Loud, at the very top, in the office's
 * own words: the whole point of the hold is that nobody books this account by
 * accident, so it cannot be a quiet chip halfway down the page.
 */
function ServiceHoldBanner({ customer }: { customer: Customer }) {
    // Opaque card (bg-card +), never a tinted transparent one:
    // the BlueprintBackdrop watermark renders behind everything and washed the
    // reason line out to unreadable at 1280 (AGENTS.md frontend rule).
    if (!customer.service_hold) return null;
    const who = customer.service_hold_set_by_name?.trim();
    const when = customer.service_hold_set_at ? formatDate(customer.service_hold_set_at) : null;
    return (
        <Card className="border-destructive/60 bg-card shadow-md">
            <CardContent className="py-4 flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-destructive/15 text-destructive flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-destructive">Do Not Service</p>
                    <p className="text-sm text-foreground mt-0.5 break-words">
                        {customer.service_hold_reason?.trim() || "No reason was recorded when this hold was placed."}
                    </p>
                    <p className="text-xs text-foreground/80 mt-1">
                        {who ? `Held by ${who}` : "Held"}
                        {when ? ` on ${when}` : ""} · New jobs warn before they're created, and renewal reminders,
                        recurring jobs and auto-scheduling are paused. Billing and collections continue.
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * Place / release the hold. Manager+ only — matching the server, which rejects
 * anyone else with 403 SERVICE_HOLD_FORBIDDEN — so lower roles see no button
 * rather than one that fails.
 */
function ServiceHoldButton({ customer }: { customer: Customer }) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");

    const mutation = useMutation({
        mutationFn: (body: { serviceHold: boolean; serviceHoldReason?: string }) =>
            api.patch(`/api/customers/${customer.id}`, body),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ["customer", customer.id] });
            qc.invalidateQueries({ queryKey: ["customers"] });
            setOpen(false);
            setReason("");
            toast.success(vars.serviceHold ? "Account placed on service hold." : "Service hold released.");
        },
        onError: (err) => {
            toast.error(err instanceof Error ? err.message : "Could not change the service hold.");
        },
    });

    if (!canManageServiceHold(user?.role)) return null;

    if (customer.service_hold) {
        return (
            <Button
                variant="outline"
                size="sm"
                disabled={mutation.isPending}
                onClick={async () => {
                    const ok = await confirmDialog({
                        title: "Release the service hold?",
                        message: `${customer.company_name} can be scheduled normally again, and renewal reminders, recurring jobs and auto-scheduling resume.`,
                        confirmLabel: "Release hold",
                    });
                    if (ok) mutation.mutate({ serviceHold: false });
                }}
            >
                <ShieldCheck className="h-4 w-4 mr-1.5" /> Release hold
            </Button>
        );
    }

    return (
        <>
            <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                <AlertTriangle className="h-4 w-4 mr-1.5" /> Do Not Service
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} title="Place on service hold">
                <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        New jobs for {customer.company_name} will warn before they're created — a manager can still push
                        one through with a reason. Renewal reminders, recurring jobs and auto-scheduling stop. Billing
                        and collections keep running.
                    </p>
                    <div>
                        <label className="text-sm font-medium" htmlFor="service-hold-reason">
                            Why? (everyone else sees this)
                        </label>
                        <Textarea
                            id="service-hold-reason"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Bad debt — 3 invoices unpaid since March."
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!reason.trim() || mutation.isPending}
                            onClick={() => mutation.mutate({ serviceHold: true, serviceHoldReason: reason.trim() })}
                        >
                            {mutation.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                            Place hold
                        </Button>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

function IntakeHandoffBanner({
    customerId,
    customerName,
    siteId,
    intakeNotes,
    similar,
    onDismiss,
}: IntakeHandoffBannerProps) {
    const navigate = useNavigate();
    const [queuedJobId, setQueuedJobId] = useState<string | null>(null);

    const queueFirstTest = useMutation({
        mutationFn: async () => {
            // Tagged with kind='first_test_visit' so dispatch can
            // cluster brand-new customers in the backlog separately
            // from the routine test_due_visit roster.
            // POST /api/jobs needs a job type (JOB_TYPE_REQUIRED), so send the
            // Backflow Test type — picked the same way Book My Test does.
            const { jobTypes } = await api.get<{ jobTypes: FirstTestJobType[] }>("/api/job-types?activeOnly=true");
            const jobType = pickBackflowTestJobType(jobTypes);
            if (!jobType) throw new Error(NO_BACKFLOW_TYPE_MESSAGE);
            const titleSuffix = customerName.length > 0 ? ` — ${customerName}` : "";
            const noteParts = ["Created from intake."];
            if (intakeNotes) noteParts.push(intakeNotes);
            return api.post<{ id: string }>("/api/jobs", {
                customerId,
                siteId,
                jobTypeId: jobType.id,
                title: `First backflow test${titleSuffix}`,
                status: "awaiting_scheduling",
                kind: "first_test_visit",
                notes: noteParts.join("\n\n"),
            });
        },
        onSuccess: (result) => {
            setQueuedJobId(result.id);
            toast.success("Queued for dispatch.");
        },
        onError: (err) => {
            toast.error(
                err instanceof Error && err.message === NO_BACKFLOW_TYPE_MESSAGE
                    ? NO_BACKFLOW_TYPE_MESSAGE
                    : "Could not queue the visit. Try again from this page.",
            );
        },
    });

    const newJobHref = `/jobs/new?customerId=${customerId}&siteId=${siteId}`;
    const newEstimateHref = `/commercial/new?kind=estimate&customerId=${customerId}&siteId=${siteId}`;

    return (
        <Card className="border-emerald-300/60 bg-gradient-to-br from-emerald-50 via-white to-primary/5 dark:from-emerald-900/20 dark:via-background dark:to-primary/10 dark:border-emerald-500/30 shadow-md">
            <CardHeader className="pb-3 flex-row items-start justify-between space-y-0">
                <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                        <Check className="h-5 w-5" />
                    </div>
                    <div>
                        <CardTitle className="text-base flex items-center gap-2">
                            {customerName} is in the system
                            <Sparkle className="h-3.5 w-3.5 text-amber-500" />
                        </CardTitle>
                        <p className="text-xs text-foreground/80 mt-0.5">
                            Pick the next step. You can always come back to this record later.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    className="text-foreground/80 hover:text-foreground transition-colors p-1 -m-1"
                >
                    <X className="h-4 w-4" />
                </button>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Primary next-step CTAs — laid out as equal-weight tiles
                    so the office isn't pushed toward one workflow. */}
                <div className="grid gap-2 sm:grid-cols-3">
                    <Link
                        to={newJobHref}
                        className={cn(
                            buttonVariants(),
                            "h-auto justify-start py-3 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
                        )}
                    >
                        <Briefcase className="h-4 w-4 shrink-0" />
                        <div className="text-left leading-tight">
                            <div className="text-sm font-semibold">Start a job</div>
                            <div className="text-[11px] opacity-90">Schedule the work</div>
                        </div>
                    </Link>
                    <Link
                        to={newEstimateHref}
                        className={cn(
                            buttonVariants({ variant: "outline" }),
                            "h-auto justify-start py-3 bg-background",
                        )}
                    >
                        <FileText className="h-4 w-4 shrink-0 text-foreground/80" />
                        <div className="text-left leading-tight">
                            <div className="text-sm font-semibold">Send a quote</div>
                            <div className="text-[11px] text-foreground/80">Estimate first</div>
                        </div>
                    </Link>
                    {queuedJobId ? (
                        <button
                            type="button"
                            onClick={() => navigate(`/jobs/${queuedJobId}`)}
                            className={cn(
                                buttonVariants({ variant: "outline" }),
                                "h-auto justify-start py-3 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30",
                            )}
                        >
                            <Check className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                            <div className="text-left leading-tight">
                                <div className="text-sm font-semibold">Open queued job</div>
                                <div className="text-[11px] text-foreground/80">Sent to dispatch</div>
                            </div>
                            <ExternalLink className="h-3.5 w-3.5 ml-auto text-foreground/80" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => queueFirstTest.mutate()}
                            disabled={queueFirstTest.isPending}
                            className={cn(
                                buttonVariants({ variant: "outline" }),
                                "h-auto justify-start py-3 bg-background",
                            )}
                        >
                            {queueFirstTest.isPending ? (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                            ) : (
                                <CalendarPlus className="h-4 w-4 shrink-0 text-foreground/80" />
                            )}
                            <div className="text-left leading-tight">
                                <div className="text-sm font-semibold">Queue first test</div>
                                <div className="text-[11px] text-foreground/80">Drop into dispatch backlog</div>
                            </div>
                        </button>
                    )}
                </div>

                {similar.length > 0 && (
                    <div className="rounded border border-amber-300/40 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
                            <div className="flex-1">
                                <div className="font-semibold text-foreground">Heads up — similar customers exist</div>
                                <p className="text-xs text-foreground/80">
                                    These may be the same business. Open and merge if needed.
                                </p>
                                <ul className="mt-2 space-y-1">
                                    {similar.map((s) => (
                                        <li
                                            key={s.id}
                                            className="flex items-center justify-between gap-2 rounded bg-background/60 px-2 py-1"
                                        >
                                            <span className="truncate">{s.company_name}</span>
                                            <Link
                                                to={`/customers/${s.id}`}
                                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline shrink-0"
                                            >
                                                Open <ExternalLink className="h-3 w-3" />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
